<template>
  <div class="read-container" :style="{ height: containerHeight }">
    <!-- Left Sidebar (Catalog + Info) -->
    <div class="left-sidebar" :class="{ 'collapsed': !showNav, 'force-open': showNav }">
      <div class="sidebar-content" :class="{ 'sidebar-content-hidden': !showNav }">
        <!-- Key Information Section -->
        <div class="key-info-section" v-if="currentPost?.post_info">
          <h2 class="collection-title">{{ collection.title }}</h2>
          <p class="collection-subtitle" v-if="collection.sub_title">{{ collection.sub_title }}</p>
          <div class="group-info">
            {{ currentGroupIndex + 1 }}. {{ currentGroup?.title }}
            <span v-if="currentGroup?.group_sub_title" class="group-sub-title"> — {{ currentGroup.group_sub_title }}</span>
          </div>
          <el-tooltip :content="currentPost?.post_info?.title" placement="bottom" :disabled="!currentPost?.post_info?.title">
            <h3 class="post-title">{{ currentPost?.post_info?.title }}</h3>
          </el-tooltip>
          <div class="quiz-summary" v-if="currentPost?.post_info?.quiz_count > 0">
            <span class="quiz-count">
              <template v-if="effectiveUserId !== collection.user_id || isInReaderMode">
                Completed {{ completedQuizCount }} of
              </template>
              {{ currentPost.post_info.quiz_count }} Quiz{{ currentPost.post_info.quiz_count > 1 ? 'zes' : '' }}
            </span>
            <div class="quiz-progress-bar" v-if="effectiveUserId !== collection.user_id || isInReaderMode">
              <div
                v-for="(_, index) in Array(currentPost.post_info.quiz_count)"
                :key="index"
                class="progress-segment"
                :class="{ 'completed': isQuizCompleted(index) }"
                @click="jumpToQuiz(index)"
              ></div>
            </div>
          </div>
        </div>

        <!-- Catalog Section -->
        <div class="catalog-section">
          <div class="catalog-header">
            <h3>Contents</h3>
            <el-tooltip :content="allGroupsExpanded ? 'Collapse All' : 'Expand All'" placement="top">
              <button class="toggle-expand-btn" @click="toggleAllGroups">
                <i :class="allGroupsExpanded ? 'el-icon-folder' : 'el-icon-folder-opened'"></i>
              </button>
            </el-tooltip>
          </div>
          <el-tree
            ref="tree"
            :data="treeData"
            :props="defaultProps"
            @node-click="handleNodeClick"
            :current-node-key="currentCompoundIndex"
            highlight-current
            node-key="compound_index"
            :default-expanded-keys="[`group-${currentGroupIndex}`]"
            empty-text="No Data"
          >
            <span class="custom-tree-node" slot-scope="{ node, data }">
              <span :class="{
                'is-group': data.type === 'group',
                'is-post': data.type === 'post',
                'is-current': data.compound_index === currentCompoundIndex || data.compound_index === `group-${currentGroupIndex}`,
                'is-paywalled': data.isPaywalled && !checkPaywallAccess(data.groupIndex, data.knowitIndex).access
              }">
                <i v-if="data.isPaywalled && !checkPaywallAccess(data.groupIndex, data.knowitIndex).access"
                   class="el-icon-lock lock-icon" style="color: #e6a23c;"></i>
                <span v-if="data.type === 'post' && data.hasUnansweredQuiz" class="unanswered-dot"></span>
                {{ node.label }}
              </span>
            </span>
          </el-tree>
        </div>
      </div>
    </div>

    <!-- Sidebar Toggle Button -->
    <div
      class="sidebar-toggle-btn"
      :class="{ 'sidebar-toggle-btn-collapsed': !showNav }"
      @click="toggleNav"
      :title="showNav ? 'Hide Catalog' : 'Show Catalog'"
    >
      <i :class="showNav ? 'el-icon-d-arrow-left' : 'el-icon-d-arrow-right'"></i>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Content Area -->
      <div class="content-area">
        <!-- Current Post Card -->
        <div class="post-card-container" :class="{ 'full-screen': isFullScreen }">
          <!-- Floating controls - top left -->
          <div class="floating-controls floating-controls-left" v-show="isCreatorWithSelfPurchase || effectiveUserId === collection.user_id || (tempUserId && tempUserId.length === 24)" :style="{ left: showNav ? '320px' : '20px' }">
            <el-tooltip
              v-if="isCreatorWithSelfPurchase"
              :content="viewMode === 'creator' ? 'Click to switch to Reader Mode' : 'Click to switch to Creator Mode'"
              placement="bottom"
            >
              <el-tag
                type="warning"
                class="floating-tag clickable"
                @click="toggleViewMode"
                size="small"
              >
                <i :class="viewMode === 'creator' ? 'el-icon-view' : 'el-icon-edit'"></i>
                {{ viewMode === 'creator' ? 'Creator' : 'Reader' }}
              </el-tag>
            </el-tooltip>
            <el-tag
              v-else-if="effectiveUserId === collection.user_id && !isCreatorWithSelfPurchase"
              type="warning"
              class="floating-tag"
              size="small"
            >Creator</el-tag>
            <el-tag
              v-if="tempUserId && tempUserId.length === 24"
              type="info"
              class="floating-tag"
              size="small"
              style="background-color: #fdf6ec; color: #e6a23c; border-color: #e6a23c;"
            >Temp</el-tag>
          </div>

          <!-- Floating controls - bottom left (Back button) -->
          <div class="floating-controls floating-controls-bottom-left" :style="{ left: showNav ? '320px' : '20px' }">
            <el-button
              icon="el-icon-back"
              class="floating-btn back-btn"
              @click="$router.back()"
            >
              Back
            </el-button>
          </div>

          <!-- Floating controls - bottom right -->
          <div class="floating-controls floating-controls-right" :style="{ right: canEditCurrentCard ? '521px' : (showAI || showNotes) ? '401px' : '20px' }">
            <el-button
              :icon="showNotes ? 'el-icon-close' : 'el-icon-notebook-2'"
              @click="toggleNotes"
              class="floating-btn note-sidebar-btn"
              :class="{ 'note-sidebar-btn-active': showNotes }"
              :title="showNotes ? 'Close Notes' : 'Open Notes'"
            >
              {{ showNotes ? 'Close' : 'Notes' }}
            </el-button>
            <el-button
              :icon="showAI ? 'el-icon-close' : 'el-icon-chat-line-square'"
              @click="toggleAI"
              class="floating-btn ai-btn"
              :class="{ 'ai-btn-active': showAI }"
              :title="showAI ? 'Close Discussion' : 'Discuss with AI'"
            >
              {{ showAI ? 'Close' : 'Ask AI' }}
            </el-button>
          </div>

          <!-- Side navigation arrows - outside content-wrapper -->
          <el-button
            v-if="currentPost"
            :disabled="!canGoPrev"
            class="side-nav-button side-nav-prev"
            :style="{ left: showNav ? '340px' : '40px' }"
            icon="el-icon-arrow-left"
            circle
            @click="prevAnswer"
          ></el-button>
          
          <el-button
            v-if="currentPost"
            :disabled="!canGoNext"
            class="side-nav-button side-nav-next"
            :style="{ right: canEditCurrentCard ? '540px' : (showAI || showNotes) ? '440px' : '40px' }"
            icon="el-icon-arrow-right"
            circle
            @click="nextAnswer"
          ></el-button>

          <div class="content-wrapper">
            <el-card class="post-card" v-if="currentPost" style="position: relative;">
              <!-- Paywall Overlay for group/post level -->
              <div class="paywall-overlay" v-if="!checkPaywallAccess(currentGroupIndex, currentPostIndex).access">
                <div class="paywall-cta">
                  <i class="el-icon-lock paywall-lock-icon"></i>
                  <h3>Subscribers Only</h3>
                  <p>This content is available to platform subscribers</p>
                  <el-button type="primary" @click="$router.push('/platform-subscription')">
                    Subscribe for Full Access
                  </el-button>
                </div>
              </div>

              <div class="post-content"
                   @mousedown="handleMouseDown"
                   @mouseup="handleMouseUp"
                   @contextmenu.prevent="handleContextMenu"
                   :class="{ 'content-blurred': !checkPaywallAccess(currentGroupIndex, currentPostIndex).access }">
                <div v-if="currentPost?.post_info?.answers?.length" class="answer-display">
                  <el-card class="answer-card" shadow="never">
                    <div class="answer-content" :key="currentAnswerIndex">
                      <template v-if="currentAnswer?.content_type === 'ending'">
                        <div class="text-content" v-html="currentAnswer.content"></div>
                      </template>
                      <div v-else-if="currentAnswer?.content_type === 'text'" class="text-content">
                        <div v-if="canEditCurrentCard && isEditingMarkdown" v-html="editingPreviewContent"></div>
                        <div v-else v-html="renderAnswerContent(currentAnswer)"></div>
                      </div>
                      <div v-else-if="currentAnswer?.content_type === 'image'" class="image-content">
                        <img :src="currentAnswer.fileList[0].url" :alt="'Content ' + (currentAnswerIndex + 1)">
                      </div>
                      <div v-else-if="currentAnswer?.content_type === 'video'" class="video-content">
                        <video controls>
                          <source :src="currentAnswer.fileList[0].url" :type="currentAnswer.fileList[0].mime_type">
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div v-else-if="currentAnswer?.content_type === 'youtube'" class="youtube-content">
                        <iframe
                          :src="'https://www.youtube.com/embed/' + extractYoutubeId(currentAnswer.content)"
                          frameborder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowfullscreen
                        ></iframe>
                      </div>
                      
                      <div v-else-if="currentAnswer?.content_type === 'quiz'" class="quiz-content">
                        <!-- Real-time preview when editing quiz -->
                        <template v-if="canEditCurrentCard && isEditingMarkdown && editingQuizContent">
                          <div class="quiz-question"><strong>Question:</strong> <span v-html="renderMarkdownContent(editingQuizContent.question)"></span></div>
                          <div
                            v-for="(option, idx) in editingQuizContent.options.filter(o => o !== '')"
                            :key="'preview-' + idx"
                            class="quiz-option not-clickable"
                            :class="{ 'correct': editingQuizContent.correct_answer && option === editingQuizContent.correct_answer }"
                          >
                            <div class="quiz-option-content">
                              <div style="display: flex; flex: 1; align-items: flex-start;">
                                <span style="min-width: 30px; line-height: 1.5;">{{ ['A','B','C','D'][idx] }}.</span>
                                <span v-html="renderMarkdownContent(option)" style="line-height: 1.5;"></span>
                              </div>
                              <span v-if="editingQuizContent.correct_answer && option === editingQuizContent.correct_answer"
                                    class="el-icon-check correct-marker"></span>
                            </div>
                          </div>
                          <div class="quiz-explanation">
                            <div class="explanation-header">Explanation:</div>
                            <div class="explanation-content" v-html="renderMarkdownContent(editingQuizContent.explanation || 'Not Available')"></div>
                          </div>
                        </template>
                        <!-- Normal quiz display — branched by quiz type -->
                        <template v-else>
                          <!-- ── SENTENCE COMPLETION ─────────────────── -->
                          <template v-if="getQuizType(currentAnswer) === 'sentence_completion'">
                            <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:var(--color-text-secondary,#909399);font-weight:600;margin-bottom:8px;">Sentence Completion</div>
                            <div style="font-size:15px;color:var(--color-text-primary,#303133);line-height:1.6;margin-bottom:16px;display:flex;flex-wrap:wrap;align-items:baseline;gap:4px;">
                              <template v-for="(part, pi) in scBlanks()">
                                <span :key="'sc-t-'+pi" v-html="renderMarkdownContent(part)"></span>
                                <button
                                  v-if="pi < scBlankCount()"
                                  :key="'sc-b-'+pi"
                                  @click="!scChecked && (effectiveUserId !== collection.user_id || isInReaderMode) && (scActiveBlank = pi)"
                                  :style="{
                                    display:'inline-flex',alignItems:'center',justifyContent:'center',minWidth:'80px',
                                    padding:'4px 12px',margin:'0 4px',borderRadius:'6px',fontSize:'13px',fontWeight:500,
                                    cursor: scChecked ? 'default' : 'pointer',
                                    border: scGetBlankResult(pi)==='correct' ? '2px solid #67C23A' :
                                            scGetBlankResult(pi)==='wrong' ? '2px solid #F56C6C' :
                                            scActiveBlank===pi ? '2px solid var(--color-brand,#409EFF)' :
                                            scSelections[pi]!==null ? '2px solid var(--color-border-lighter,#ebeef5)' :
                                            '2px dashed var(--color-border,#dcdfe6)',
                                    background: scGetBlankResult(pi)==='correct' ? '#f0f9eb' :
                                                scGetBlankResult(pi)==='wrong' ? '#fef0f0' :
                                                scActiveBlank===pi ? 'var(--color-brand-bg,#ecf5ff)' :
                                                scSelections[pi]!==null ? 'var(--color-bg-section,#f5f7fa)' : 'transparent',
                                    color: scGetBlankResult(pi)==='correct' ? '#67C23A' :
                                           scGetBlankResult(pi)==='wrong' ? '#F56C6C' :
                                           scActiveBlank===pi ? 'var(--color-brand,#409EFF)' :
                                           'var(--color-text-primary,#303133)',
                                  }"
                                >{{ scSelections[pi] !== null ? (currentAnswer.quiz_content.options[scSelections[pi]]) : ('blank ' + (pi+1)) }}</button>
                              </template>
                            </div>
                            <!-- Option picker for active blank -->
                            <div v-if="scActiveBlank !== null && !scChecked" style="margin-bottom:16px;padding:12px;background:var(--color-bg-section,#f5f7fa);border-radius:8px;border:1px solid var(--color-border-extra-light,#f2f6fc);">
                              <p style="font-size:12px;color:var(--color-text-secondary,#909399);margin-bottom:8px;">Select a word for blank {{ scActiveBlank + 1 }}:</p>
                              <div style="display:flex;flex-wrap:wrap;gap:8px;">
                                <button
                                  v-for="(opt, oi) in currentAnswer.quiz_content.options"
                                  :key="oi"
                                  :disabled="scUsedOptions().has(oi) && scSelections[scActiveBlank] !== oi"
                                  @click="scSelectOption(oi)"
                                  :style="{
                                    padding:'6px 12px',fontSize:'13px',borderRadius:'6px',cursor:'pointer',
                                    border:'1px solid var(--color-border-lighter,#ebeef5)',
                                    opacity: (scUsedOptions().has(oi) && scSelections[scActiveBlank] !== oi) ? 0.4 : 1,
                                    color:'var(--color-text-primary,#303133)',background:'white'
                                  }"
                                >{{ opt }}</button>
                              </div>
                            </div>
                            <button
                              v-if="!scChecked && (effectiveUserId !== collection.user_id || isInReaderMode)"
                              :disabled="scSelections.some(s => s === null)"
                              @click="scCheckAnswers()"
                              style="padding:8px 20px;background:var(--color-brand,#409EFF);color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;"
                              :style="{ opacity: scSelections.some(s => s === null) ? 0.4 : 1 }"
                            >Check Answers</button>
                            <div v-if="scChecked" style="margin-top:12px;font-size:13px;font-weight:500;">
                              You got <span :style="{ color: scCorrectCount() === scBlankCount() ? '#67C23A' : '#E6A23C' }">{{ scCorrectCount() }}/{{ scBlankCount() }}</span> correct.
                            </div>
                            <div v-if="scChecked && currentAnswer.quiz_content.explanation" class="quiz-explanation">
                              <div class="explanation-header">Explanation:</div>
                              <div class="explanation-content" v-html="renderMarkdownContent(currentAnswer.quiz_content.explanation)"></div>
                            </div>
                          </template>

                          <!-- ── WORD SPELLING ───────────────────────── -->
                          <template v-else-if="getQuizType(currentAnswer) === 'word_spelling'">
                            <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:var(--color-text-secondary,#909399);font-weight:600;margin-bottom:8px;">Spelling</div>
                            <div style="font-size:15px;font-weight:500;color:var(--color-text-primary,#303133);line-height:1.6;margin-bottom:20px;" v-html="renderMarkdownContent(currentAnswer.quiz_content.question || '')"></div>
                            <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:16px;">
                              <input
                                v-for="(letter, li) in spLetters"
                                :key="li"
                                type="text"
                                maxlength="1"
                                :value="letter"
                                :readonly="spIsHintOrRevealed(li) || spChecked || (effectiveUserId === collection.user_id && !isInReaderMode)"
                                @input="spHandleChange(li, $event.target.value); spHandleInput(li, $event)"
                                @keydown="spHandleKeyDown(li, $event)"
                                :style="{
                                  width:'40px',height:'48px',textAlign:'center',fontSize:'18px',fontWeight:700,
                                  borderRadius:'6px',border:'2px solid',textTransform:'uppercase',outline:'none',
                                  borderColor: spIsHintOrRevealed(li) ? 'var(--color-brand-border,#b3d8ff)' :
                                               spLetterResult(li)==='correct' ? '#67C23A' :
                                               spLetterResult(li)==='wrong' ? '#F56C6C' :
                                               'var(--color-border-lighter,#ebeef5)',
                                  background: spIsHintOrRevealed(li) ? 'var(--color-brand-bg,#ecf5ff)' :
                                              spLetterResult(li)==='correct' ? '#f0f9eb' :
                                              spLetterResult(li)==='wrong' ? '#fef0f0' : '#fff',
                                  color: spIsHintOrRevealed(li) ? 'var(--color-brand,#409EFF)' :
                                         spLetterResult(li)==='correct' ? '#67C23A' :
                                         spLetterResult(li)==='wrong' ? '#F56C6C' :
                                         'var(--color-text-primary,#303133)',
                                }"
                              />
                            </div>
                            <div v-if="!spChecked && (effectiveUserId !== collection.user_id || isInReaderMode)" style="display:flex;gap:10px;align-items:center;">
                              <button
                                :disabled="spLetters.some(l => !l)"
                                @click="spCheck()"
                                style="padding:8px 20px;background:var(--color-brand,#409EFF);color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;"
                                :style="{ opacity: spLetters.some(l => !l) ? 0.4 : 1 }"
                              >Check</button>
                              <button
                                v-if="spHintLevel < 4"
                                @click="spUseHint()"
                                style="padding:8px 16px;background:#fff;color:var(--color-text-secondary,#909399);border:1px solid var(--color-border-lighter,#ebeef5);border-radius:6px;font-size:12px;cursor:pointer;"
                              >💡 Hint ({{ 4 - spHintLevel }} left)</button>
                              <span v-if="spHintLevel === 4" style="font-size:12px;color:#E6A23C;font-weight:500;">Answer revealed</span>
                            </div>
                            <div v-if="spChecked" style="margin-top:12px;font-size:13px;font-weight:500;" :style="{ color: spIsCorrect() ? '#67C23A' : '#F56C6C' }">
                              <template v-if="spIsCorrect()">Correct!</template>
                              <template v-else>Incorrect. The answer is <strong>{{ currentAnswer.quiz_content.correct_answer }}</strong>.</template>
                            </div>
                            <div v-if="spChecked && currentAnswer.quiz_content.explanation" class="quiz-explanation">
                              <div class="explanation-header">Explanation:</div>
                              <div class="explanation-content" v-html="renderMarkdownContent(currentAnswer.quiz_content.explanation)"></div>
                            </div>
                          </template>

                          <!-- ── MATCHING ────────────────────────────── -->
                          <template v-else-if="getQuizType(currentAnswer) === 'matching'">
                            <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:var(--color-text-secondary,#909399);font-weight:600;margin-bottom:8px;">Matching</div>
                            <!-- Sub-view: right-column options for active left item -->
                            <template v-if="matchActiveLeft !== null && !matchChecked">
                              <button @click="matchActiveLeft = null" style="font-size:12px;color:var(--color-brand-light,#409EFF);cursor:pointer;border:none;background:none;margin-bottom:8px;">← Back to list</button>
                              <div style="font-size:13px;font-weight:500;color:var(--color-text-primary,#303133);margin-bottom:12px;padding:12px;background:var(--color-brand-bg,#ecf5ff);border-radius:6px;border:1px solid var(--color-brand-border,#b3d8ff);">
                                Match for: <strong>{{ currentAnswer.quiz_content.left_items[matchActiveLeft] }}</strong>
                              </div>
                              <div style="display:flex;flex-direction:column;gap:8px;">
                                <button
                                  v-for="(item, ri) in currentAnswer.quiz_content.right_items"
                                  :key="ri"
                                  :disabled="matchUsedRight().has(ri) && matchPairs[matchActiveLeft] !== ri"
                                  @click="matchSelectRight(ri)"
                                  :style="{
                                    width:'100%',textAlign:'left',padding:'12px',borderRadius:'8px',fontSize:'13px',cursor:'pointer',
                                    border: matchPairs[matchActiveLeft] === ri ? '2px solid var(--color-brand,#409EFF)' : '2px solid var(--color-border-lighter,#ebeef5)',
                                    background: matchPairs[matchActiveLeft] === ri ? 'var(--color-brand-bg,#ecf5ff)' : '#fff',
                                    opacity: (matchUsedRight().has(ri) && matchPairs[matchActiveLeft] !== ri) ? 0.4 : 1,
                                    color:'var(--color-text-primary,#303133)',
                                  }"
                                >{{ item }}</button>
                              </div>
                            </template>
                            <!-- Default view: left column items -->
                            <template v-else>
                              <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:16px;">
                                <button
                                  v-for="(item, li) in currentAnswer.quiz_content.left_items"
                                  :key="li"
                                  @click="!matchChecked && (effectiveUserId !== collection.user_id || isInReaderMode) && matchSelectLeft(li)"
                                  :style="{
                                    width:'100%',textAlign:'left',padding:'14px',borderRadius:'8px',fontSize:'13px',
                                    cursor: matchChecked ? 'default' : 'pointer',
                                    border: matchGetPairResult(li)==='correct' ? '2px solid #67C23A' :
                                            matchGetPairResult(li)==='wrong' ? '2px solid #F56C6C' :
                                            '2px solid var(--color-border-lighter,#ebeef5)',
                                    background: matchGetPairResult(li)==='correct' ? '#f0f9eb' :
                                                matchGetPairResult(li)==='wrong' ? '#fef0f0' : '#fff',
                                    color:'var(--color-text-primary,#303133)',
                                  }"
                                >
                                  <span style="font-weight:500;">{{ item }}</span>
                                  <span v-if="matchPairs[li] !== undefined" style="display:block;margin-top:4px;font-size:12px;color:var(--color-text-secondary,#909399);">
                                    → {{ currentAnswer.quiz_content.right_items[matchPairs[li]] }}
                                    <span v-if="matchGetPairResult(li)==='correct'" style="margin-left:8px;color:#67C23A;font-weight:700;">✓</span>
                                    <span v-if="matchGetPairResult(li)==='wrong'" style="margin-left:8px;">
                                      <span style="color:#F56C6C;font-weight:700;">✗</span>
                                      <span style="margin-left:4px;color:#67C23A;">({{ currentAnswer.quiz_content.right_items[Object.fromEntries((currentAnswer.quiz_content.correct_answer||[]).map(([l,r])=>[l,r]))[li]] }})</span>
                                    </span>
                                  </span>
                                </button>
                              </div>
                              <button
                                v-if="!matchChecked && (effectiveUserId !== collection.user_id || isInReaderMode)"
                                :disabled="!matchAllPaired()"
                                @click="matchCheck()"
                                style="padding:8px 20px;background:var(--color-brand,#409EFF);color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;"
                                :style="{ opacity: matchAllPaired() ? 1 : 0.4 }"
                              >Check Answers</button>
                              <div v-if="matchChecked" style="margin-top:12px;font-size:13px;font-weight:500;">
                                You got <span :style="{ color: matchCorrectCount() === (currentAnswer.quiz_content.left_items||[]).length ? '#67C23A' : '#E6A23C' }">{{ matchCorrectCount() }}/{{ (currentAnswer.quiz_content.left_items||[]).length }}</span> correct.
                              </div>
                              <div v-if="matchChecked && currentAnswer.quiz_content.explanation" class="quiz-explanation">
                                <div class="explanation-header">Explanation:</div>
                                <div class="explanation-content" v-html="renderMarkdownContent(currentAnswer.quiz_content.explanation)"></div>
                              </div>
                            </template>
                          </template>

                          <!-- ── MULTIPLE CHOICE (default) ───────────── -->
                          <template v-else>
                            <div class="quiz-question"><strong>Question:</strong> <span v-html="renderMarkdownContent(currentAnswer?.quiz_content?.question || '')"></span></div>
                            <div
                              v-for="(option, idx) in (currentAnswer?.quiz_content?.options || []).filter(option => option !== '')"
                              :key="idx"
                              class="quiz-option"
                              :class="{
                                'selected': selectedAnswer === idx,
                                'correct': (isQuizAnswered(currentAnswer.uniqueCode) ||
                                  (effectiveUserId === collection.user_id && !isInReaderMode)) && ['A','B','C','D'][idx] === getCorrectAnswerABCD(currentAnswer?.quiz_content),
                                'incorrect': isQuizAnswered(currentAnswer.uniqueCode) &&
                                  getQuizAnswer(currentAnswer.uniqueCode)?.['answer'] !== getCorrectAnswerABCD(currentAnswer?.quiz_content) &&
                                  (effectiveUserId !== collection.user_id || isInReaderMode) &&
                                  ['A','B','C','D'][idx] === getQuizAnswer(currentAnswer.uniqueCode)?.['answer'],
                                'not-clickable': isQuizAnswered(currentAnswer.uniqueCode) ||
                                  (effectiveUserId === collection.user_id && !isInReaderMode)
                              }"
                              @click="((effectiveUserId !== collection.user_id || isInReaderMode) && !isQuizAnswered(currentAnswer.uniqueCode)) && selectAnswer(idx)"
                            >
                              <div class="quiz-option-content">
                                <div style="display: flex; flex: 1; align-items: flex-start;">
                                  <span style="min-width: 30px; line-height: 1.5;">{{ ['A','B','C','D'][idx] }}.</span>
                                  <span v-html="renderMarkdownContent(option)" style="line-height: 1.5;"></span>
                                </div>
                                <span v-if="(isQuizAnswered(currentAnswer.uniqueCode) ||
                                            (effectiveUserId === collection.user_id && !isInReaderMode)) &&
                                            ['A','B','C','D'][idx] === getCorrectAnswerABCD(currentAnswer?.quiz_content)"
                                      class="el-icon-check correct-marker">
                                </span>
                                <span v-if="isQuizAnswered(currentAnswer.uniqueCode) &&
                                            ['A','B','C','D'][idx] === getQuizAnswer(currentAnswer.uniqueCode)?.['answer'] &&
                                            getQuizAnswer(currentAnswer.uniqueCode)?.['answer'] !== getCorrectAnswerABCD(currentAnswer?.quiz_content)"
                                      class="el-icon-close incorrect-marker">
                                </span>
                              </div>
                            </div>
                            <div v-if="isQuizAnswered(currentAnswer.uniqueCode) || (effectiveUserId === collection.user_id && !isInReaderMode)" class="quiz-explanation">
                              <div class="explanation-header">Explanation:</div>
                              <div class="explanation-content" v-html="renderMarkdownContent(currentAnswer?.quiz_content?.explanation || 'Not Available')"></div>
                            </div>
                          </template>
                        </template>
                      </div>
                    </div>
                    
                  </el-card>
                  <div class="card-footer">
                    <div class="answer-actions">
                      <span
                        class="word-count-chip"
                        v-if="currentCardWordCount > 0"
                        :title="currentCardCharCount + ' characters · ' + currentCardWordCount + ' words'"
                      >
                        {{ currentCardWordCount }} {{ currentCardWordCount === 1 ? 'word' : 'words' }} · {{ currentCardCharCount }} chars
                      </span>
                      <el-button
                        :disabled="effectiveUserId === collection.user_id && !isInReaderMode"
                        type="text"
                        class="star-button"
                        @click.stop="toggleStar"
                        :class="{ 'is-starred': isAnswerStarred }"
                      >
                        <i class="el-icon-star-on"></i>
                      </el-button>
                      <el-button
                        type="text"
                        class="fullscreen-button"
                        @click.stop="toggleFullScreen"
                        :title="isFullScreen ? 'Exit Full Screen' : 'Full Screen'"
                      >
                        <i :class="isFullScreen ? 'el-icon-close' : 'el-icon-full-screen'"></i>
                      </el-button>
                    </div>
                  </div>
                </div>
                  
                
              </div>
              
              


            </el-card>

            <!-- Answer Controls - Horizontal Paginator Below Card -->
            <div class="answer-controls" :class="{ 'placeholder-controls': !currentPost?.post_info?.answers?.length }" style="padding: 0; gap: 0; margin-top: 2px;">
              <template v-if="currentPost?.post_info?.answers?.length">
                <span class="answer-number" style="margin: 0 0 2px 0; font-size: 11px;">
                  #{{ currentAnswerIndex + 1 }} of {{ currentPost.post_info.answers.length }}
                </span>
                <div class="navigation-row" style="gap: 6px;">
                  <div class="answer-navigation" style="gap: 5px;">
                    <span
                      v-for="(_, index) in currentPost.post_info.answers"
                      :key="index"
                      :class="['nav-dot', { active: index === currentAnswerIndex }]"
                      @click="goToAnswer(index)"
                    ></span>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="answer-navigation"></div>
              </template>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar for Open Discussion -->
    <div class="right-sidebar" :class="{ 'collapsed': !showAI }">
      <div class="sidebar-content">
        <PostQAPanel
          :postId="currentPostIdString"
          :pageContent="currentPageContent"
          :cardId="currentAnswer?.uniqueCode || ''"
          :cardContent="currentCardContent"
          :collectionTitle="collection.title || ''"
          :userId="effectiveUserId || $store.state.user_id || ''"
        />
      </div>
    </div>

    <!-- Right Sidebar for Study Notes -->
    <div class="right-sidebar note-sidebar" :class="{ 'collapsed': !showNotes }">
      <div class="sidebar-content note-sidebar-inner">
        <div class="note-sidebar-header">
          <div>
            <h3>Study Notes</h3>
            <p class="note-sidebar-hint">Here you can add private study notes.</p>
          </div>
          <button class="close-btn" @click="showNotes = false">&times;</button>
        </div>
        <div class="note-sidebar-body">
          <div v-if="currentCardNotes.length === 0" class="note-empty">
            <i class="el-icon-notebook-2" style="font-size: 36px; color: #c0c4cc; margin-bottom: 12px;"></i>
            <p>No notes for this card yet.</p>
          </div>
          <div v-else class="note-list">
            <div v-for="(note, idx) in currentCardNotes" :key="idx" class="note-card"
                 :class="{ 'note-card-editing': editingSidebarNoteIndex === idx }">
              <div class="note-card-content">{{ note.note_content }}</div>
              <div class="note-card-footer">
                <span class="note-card-time" v-if="note.latest_update_time">
                  {{ new Date(note.latest_update_time * 1000).toLocaleString() }}
                </span>
                <div class="note-card-actions">
                  <el-button type="text" size="mini" icon="el-icon-edit" @click="editSidebarNote(idx)"></el-button>
                  <el-button type="text" size="mini" icon="el-icon-delete" @click="deleteSidebarNote(idx)"></el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="note-sidebar-input">
          <el-input
            type="textarea"
            v-model="sidebarNoteContent"
            :rows="3"
            :placeholder="editingSidebarNoteIndex !== null ? 'Editing note...' : 'Write your note here...'"
            resize="none"
            @keydown.native.stop
          ></el-input>
          <div class="note-sidebar-input-actions">
            <el-button v-if="editingSidebarNoteIndex !== null" size="small" @click="cancelEditSidebarNote">Cancel</el-button>
            <el-button type="primary" size="small" @click="saveSidebarNote" :disabled="!sidebarNoteContent.trim()">
              {{ editingSidebarNoteIndex !== null ? 'Update' : 'Save' }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar for Markdown Editor -->
    <div class="right-sidebar markdown-editor-sidebar" :class="{ 'collapsed': !canEditCurrentCard }">
      <div class="sidebar-content markdown-editor-inner">
        <div class="markdown-editor-header">
          <div>
            <h3>Markdown</h3>
            <p class="markdown-editor-hint" v-if="!isEditingMarkdown">Click "Edit" to modify content</p>
            <p class="markdown-editor-hint" v-else>Editing… live preview on the left</p>
          </div>
          <div class="markdown-editor-header-actions">
            <el-button
              v-if="!isEditingMarkdown"
              type="primary"
              size="small"
              icon="el-icon-edit"
              @click="startEditMarkdown"
            >Edit</el-button>
            <template v-else>
              <el-button
                size="small"
                @click="cancelEditMarkdown"
                :disabled="isSavingMarkdown"
              >Cancel</el-button>
              <el-button
                type="primary"
                size="small"
                icon="el-icon-check"
                @click="saveMarkdown"
                :loading="isSavingMarkdown"
              >Save</el-button>
            </template>
          </div>
        </div>
        <div class="markdown-editor-body">
          <div v-if="!isEditingMarkdown" class="markdown-readonly">
            <pre class="markdown-source">{{ currentCardMarkdown }}</pre>
          </div>
          <textarea
            v-else
            class="markdown-textarea"
            v-model="editingMarkdownContent"
            @keydown.stop
            @keydown.tab.prevent="insertTab"
            placeholder="输入 Markdown 内容..."
          ></textarea>
        </div>
      </div>
    </div>

    <!-- Custom Settings Modal -->
    <div v-if="showSettingsDialog" class="custom-modal-overlay" @click="showSettingsDialog = false">
      <div class="custom-modal" @click.stop>
        <div class="modal-header">
          <h3>Reading Settings</h3>
          <button class="close-btn" @click="showSettingsDialog = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="no-settings">
            <p>No special reading settings configured for this course.</p>
          </div>
        </div>
        <div class="modal-footer">
          <el-button @click="showSettingsDialog = false">Close</el-button>
        </div>
      </div>
    </div>
  </div>

  

</template>



<script>
import { postRequest } from '@/apis/front'
import PostQAPanel from '@/components/PostQAPanel'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { renderLatex } from '@/utils/latexRenderer'

export default {
  name: 'ReadCollection',
  components: {
    PostQAPanel
  },
  data() {
    return {
      collection: {},
      showNav: true,
      showAI: false,
      showNotes: false,
      allGroupsExpanded: false,
      sidebarNoteContent: '',
      editingSidebarNoteIndex: null,
      manuallyClosedNav: false,
      currentPostId: null,
      currentCompoundIndex: '0-0',
      currentGroupIndex: 0,
      currentPostIndex: 0,
      defaultProps: {
        children: 'children',
        label: 'label'
      },
      currentAnswerIndex: 0,
      selectedAnswer: null,
      localQuizAnswers: {}, // { uniqueCode: { answer: 'A' } } for instant feedback
      showAnswer: false,
      // Sentence Completion quiz state
      scSelections: [],
      scActiveBlank: null,
      scChecked: false,
      // Word Spelling quiz state
      spLetters: [],
      spChecked: false,
      spHintLevel: 0, // 0=none, 1=first letter, 2=first+last, 3=first+last+middle, 4=full word (give up)
      // Matching quiz state
      matchPairs: {},
      matchActiveLeft: null,
      matchChecked: false,
      starredAnswers: new Set(),
      starredPostIds: {},
      cardStarStatus: {}, // Track card star status by post_id
      editNote:{
        content_type:"note",
        note_type:"text",
        post_id: null,
        uniqueCode: null,
        note_content: '',
        quote_content: '',
      },
      isEditingNote: false,
      mouseDownPos: null,
      mouseDownTime: null,
      authCode: null,
      tempUserId: null,
      effectiveUserId: null,
      isFullScreen: false,
      answersToShow:[],
      showSettingsDialog: false,
      viewMode: 'creator', // 'creator' or 'reader'
      lastClickTime: 0, // Track last click time for double-click detection
      // Markdown editor state
      editingMarkdownContent: '',
      isEditingMarkdown: false, // false = read-only preview, true = editing
      isSavingMarkdown: false
    }
  },
  computed: {
    treeData() {
      if (!this.collection?.groups) return []

      return this.collection.groups.map((group, groupIndex) => ({
        label: group.title || 'Untitled Group',
        type: 'group',
        groupIndex,
        compound_index: `group-${groupIndex}`,
        isPaywalled: !!group.subscribers_only,
        children: (group.knowits || []).map((knowit, knowitIndex) => {
          const quizCount = knowit?.post_info?.quiz_count || 0
          const answeredCount = Object.keys(knowit?.post_info?.view_info?.quiz_answers || {}).length
          return {
            label: knowit?.post_info?.title || 'Untitled',
            type: 'post',
            groupIndex,
            knowitIndex,
            id: knowit.knowit_id,
            compound_index: `${groupIndex}-${knowitIndex}`,
            hasUnansweredQuiz: quizCount > 0 && answeredCount < quizCount,
            isPaywalled: !!knowit.subscribers_only || !!group.subscribers_only
          }
        })
      }))
    },
    currentGroup() {
      return this.collection?.groups?.[this.currentGroupIndex] || null
    },
    currentPost() {
      console.log("Current Post: ", this.currentGroup?.knowits?.[this.currentPostIndex])
      return this.currentGroup?.knowits?.[this.currentPostIndex] || null
    },
    currentAnswer() {
      return this.currentPost?.post_info?.answers?.[this.currentAnswerIndex] || null
    },
    currentCardNotes() {
      const uniqueCode = this.currentAnswer?.uniqueCode
      if (!uniqueCode) return []
      const noteArray = this.currentPost?.post_info?.view_info?.note_array
      if (!noteArray) return []
      return noteArray[uniqueCode] || []
    },
    isAnswerStarred() {
      const starredAnswers = this.currentPost?.post_info?.view_info?.starred_answers
      if (!starredAnswers) return false
      const uniqueCode = this.currentAnswer?.uniqueCode
      if (!uniqueCode) return false
      return !!starredAnswers[uniqueCode]
    },
    currentPostIdString() {
      const id = this.currentPost?.post_info?._id
      if (!id) return ''
      return id.$oid || id.$id || ''
    },
    completedQuizCount() {
      if (!this.currentPost?.post_info?.answers) return 0
      const serverAnswers = this.currentPost.post_info.view_info?.quiz_answers || {}
      const quizzes = this.currentPost.post_info.answers.filter(a => a.content_type === 'quiz')
      return quizzes.filter(q => serverAnswers[q.uniqueCode] || this.localQuizAnswers[q.uniqueCode]).length
    },
    currentPageContent() {
      const answers = this.currentPost?.post_info?.answers
      if (!answers?.length) return ''
      return answers
        .filter(a => a.content_type === 'text' && a.content)
        .map(a => a.content.replace(/<[^>]*>/g, ''))
        .join('\n\n')
    },
    currentCardContent() {
      const answer = this.currentAnswer
      if (!answer) return ''
      if (answer.content_type === 'text' && answer.content) {
        return answer.content.replace(/<[^>]*>/g, '')
      }
      if (answer.content_type === 'quiz' && answer.quiz_content) {
        let text = answer.quiz_content.question || ''
        if (answer.quiz_content.options) {
          text += '\n' + answer.quiz_content.options.filter(o => o).map((o, i) => `${['A','B','C','D'][i]}. ${o}`).join('\n')
        }
        if (answer.quiz_content.explanation) {
          text += '\n' + answer.quiz_content.explanation
        }
        return text
      }
      return ''
    },
    currentCardWordCount() {
      const text = this.currentCardContent
      if (!text) return 0
      const trimmed = text.trim()
      if (!trimmed) return 0
      const cjkRegex = /[\u4e00-\u9fff\u3040-\u30ff\uac00-\ud7af]/g
      const cjkMatches = trimmed.match(cjkRegex)
      const cjkCount = cjkMatches ? cjkMatches.length : 0
      const nonCjk = trimmed.replace(cjkRegex, ' ').trim()
      let wordCount = 0
      if (nonCjk) {
        wordCount = nonCjk.split(/\s+/).filter(w => w.length > 0).length
      }
      return cjkCount + wordCount
    },
    currentCardCharCount() {
      const text = this.currentCardContent
      if (!text) return 0
      return text.replace(/\s/g, '').length
    },
    isCreatorWithSelfPurchase() {
      return this.effectiveUserId === this.collection.user_id &&
             this.collection.order_info?.user_id === this.$store.state.user_id
    },
    isInReaderMode() {
      return this.viewMode === 'reader'
    },
    containerHeight() {
      // 始终使用100vh,导航栏将覆盖在页面上方
      return '100vh'
    },
    canGoPrev() {
      // Can go prev if not at the first answer of the first post of the first group
      if (this.currentAnswerIndex > 0) return true
      if (this.currentPostIndex > 0) return true
      if (this.currentGroupIndex > 0) return true
      return false
    },
    canGoNext() {
      // Can go next if not at the last answer of the last post of the last group
      if (this.currentAnswerIndex < (this.currentPost?.post_info?.answers?.length - 1)) return true
      if (this.currentPostIndex < (this.currentGroup?.knowits?.length - 1)) return true
      if (this.currentGroupIndex < (this.collection?.groups?.length - 1)) return true
      return false
    },
    isCreatorMode() {
      return this.effectiveUserId === this.collection.user_id && !this.isInReaderMode
    },
    canEditCurrentCard() {
      if (!this.isCreatorMode) return false
      const answer = this.currentAnswer
      if (!answer) return false
      return answer.content_type === 'text' || answer.content_type === 'quiz'
    },
    // Serialize current card content for display in markdown panel (read-only)
    currentCardMarkdown() {
      const answer = this.currentAnswer
      if (!answer) return ''
      if (answer.content_type === 'text') return answer.content || ''
      if (answer.content_type === 'quiz') return this.serializeQuizContent(answer.quiz_content)
      return ''
    },
    // Parsed quiz content from editing textarea (for real-time preview)
    editingQuizContent() {
      if (!this.isEditingMarkdown || this.currentAnswer?.content_type !== 'quiz') return null
      return this.parseQuizContent(this.editingMarkdownContent)
    },
    // Content for real-time preview when editing text cards
    editingPreviewContent() {
      if (!this.canEditCurrentCard || !this.isEditingMarkdown) return ''
      const answer = this.currentAnswer
      if (!answer) return ''
      if (answer.content_type === 'text') {
        return this.renderAnswerContent({ ...answer, content: this.editingMarkdownContent })
      }
      return ''
    }
  },
  watch: {
    currentAnswer() {
      // When card changes, reset editing state
      if (this.isEditingMarkdown) {
        this.isEditingMarkdown = false
        this.editingMarkdownContent = ''
      }
      // Reset quiz-type-specific state for the new card
      this.resetQuizTypeState()
    }
  },
  created() {
    console.log('ReadCollection created, user_id:', this.$store.state.user_id)

    // Set initial viewMode based on source query parameter
    const source = this.$route.query.source
    if (source === 'acquisitions') {
      this.viewMode = 'reader'
    } else if (source === 'created') {
      this.viewMode = 'creator'
    }
    // If no source specified, keep default 'creator'

    // Restore collection ID from localStorage if not in store
    if (!this.$store.state.tempCollectionId && !this.$route.query.collection_id) {
      const savedCollectionId = localStorage.getItem('currentCollectionId')
      if (savedCollectionId) {
        this.$store.dispatch('setTempCollectionId', savedCollectionId)
        console.log('Restored collection ID from localStorage:', savedCollectionId)
      }
    }

    this.authCode = this.$route.query.auth
    if (!this.$store.state.user_id && this.authCode) {
      console.log('Processing auth code...')
      this.processAuthCode()
    } else {
      this.effectiveUserId = this.$store.state.user_id;
      console.log('Effective user ID:', this.effectiveUserId)
      this.fetchCollectionData()
    }
  },
  mounted() {
    // Add both event listeners
    window.addEventListener('keydown', this.handleKeyNavigation)
    window.addEventListener('resize', this.handleResize)

    // Set initial sidebar state based on screen width
    this.handleResize()

    // Add fullscreen change event listener
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement) {
        this.isFullScreen = false;
      }
    });

  },
  
  beforeDestroy() {
    // Remove both event listeners
    window.removeEventListener('keydown', this.handleKeyNavigation)
    window.removeEventListener('resize', this.handleResize)
    
    // Remove fullscreen change event listener
    document.removeEventListener('fullscreenchange', () => {});
    
    // Clean up the temporary collection ID from store
    // this.$store.dispatch('setTempCollectionId', null)
  },
  methods: {
    getCorrectAnswerABCD(quiz_content) {
      if (!quiz_content || !quiz_content.options || !quiz_content.correct_answer) {
        return null
      }
      const correct_answer_index = quiz_content.options.indexOf(quiz_content.correct_answer)
      if (correct_answer_index === -1) {
        return quiz_content.correct_answer
      }
      return ['A','B','C','D'][correct_answer_index]
    },

    renderMarkdownContent(content) {
      if (!content) return ''

      // 先转义 HTML 标签，使其显示为文本
      const escapeHtml = (text) => {
        return text
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
      }

      // KaTeX allowed tags for LaTeX rendering
      const katexTags = ['span', 'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac', 'munder', 'mover', 'msqrt', 'mroot', 'mtable', 'mtr', 'mtd', 'annotation', 'svg', 'line', 'path', 'rect', 'g']
      const katexAttrs = ['class', 'style', 'aria-hidden', 'xmlns', 'width', 'height', 'viewBox', 'preserveAspectRatio', 'd', 'fill', 'stroke', 'x', 'y', 'x1', 'x2', 'y1', 'y2', 'encoding']

      // Try to detect and render markdown
      const hasMarkdownPatterns = this.detectMarkdownPatterns(content)

      if (hasMarkdownPatterns) {
        try {
          marked.setOptions({
            breaks: true,
            gfm: true,
            headerIds: false,
            mangle: false
          })
          let rawHtml = marked(this.escapeSingleTildes(content))
          // Render LaTeX formulas
          rawHtml = renderLatex(rawHtml)
          // 使用 ALLOWED_TAGS 为空数组来移除所有用户输入的 HTML 标签，只保留 markdown 生成的标签
          return DOMPurify.sanitize(rawHtml, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'mark', 'hr', ...katexTags],
            ADD_ATTR: ['target', 'rel', ...katexAttrs]
          })
        } catch (e) {
          console.warn('Markdown parsing failed', e)
        }
      }

      // Render LaTeX in plain text content
      let result = escapeHtml(content).replace(/\n/g, '<br>')
      result = renderLatex(result)
      return DOMPurify.sanitize(result, {
        ALLOWED_TAGS: ['br', ...katexTags],
        ADD_ATTR: katexAttrs
      })
    },
    
    extractYoutubeId(url) {
      if (!url) return ''
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
        /^([a-zA-Z0-9_-]{11})$/
      ]
      for (const pattern of patterns) {
        const match = url.match(pattern)
        if (match) return match[1]
      }
      return ''
    },

    renderAnswerContent(answer) {
      if (!answer || !answer.content) return ''

      // KaTeX allowed tags for LaTeX rendering
      const katexTags = ['span', 'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac', 'munder', 'mover', 'msqrt', 'mroot', 'mtable', 'mtr', 'mtd', 'annotation', 'svg', 'line', 'path', 'rect', 'g']
      const katexAttrs = ['class', 'style', 'aria-hidden', 'xmlns', 'width', 'height', 'viewBox', 'preserveAspectRatio', 'd', 'fill', 'stroke', 'x', 'y', 'x1', 'x2', 'y1', 'y2', 'encoding']

      // Check the editor type
      const editorType = answer.editor_type || 'auto'

      // If editor type is explicitly set, use it
      if (editorType === 'markdown') {
        marked.setOptions({
          breaks: true,
          gfm: true,
          headerIds: false,
          mangle: false
        })
        let rawHtml = marked(this.escapeSingleTildes(answer.content))
        rawHtml = renderLatex(rawHtml)
        return DOMPurify.sanitize(rawHtml, {
          ADD_TAGS: ['mark', ...katexTags],
          ADD_ATTR: ['target', 'rel', ...katexAttrs]
        })
      } else if (editorType === 'richtext') {
        let result = renderLatex(answer.content)
        return DOMPurify.sanitize(result, {
          ADD_TAGS: katexTags,
          ADD_ATTR: katexAttrs
        })
      } else {
        // Auto-detect content type
        const content = answer.content
        const hasMarkdownPatterns = this.detectMarkdownPatterns(content)
        const hasHtmlTags = this.detectHtmlTags(content)

        // If markdown patterns detected (check first before HTML), render as markdown
        if (hasMarkdownPatterns) {
          try {
            marked.setOptions({
              breaks: true,
              gfm: true,
              headerIds: false,
              mangle: false
            })
            let rawHtml = marked(this.escapeSingleTildes(content))
            rawHtml = renderLatex(rawHtml)
            // Always use markdown rendering if patterns are detected
            return DOMPurify.sanitize(rawHtml, {
              ADD_TAGS: ['mark', ...katexTags],
              ADD_ATTR: ['target', 'rel', ...katexAttrs]
            })
          } catch (e) {
            console.warn('Markdown parsing failed, falling back to HTML or plain text', e)
          }
        }

        // If HTML tags detected, treat as rich text
        if (hasHtmlTags) {
          let result = renderLatex(content)
          return DOMPurify.sanitize(result, {
            ADD_TAGS: katexTags,
            ADD_ATTR: katexAttrs
          })
        }

        // Fall back to plain text with line breaks
        let escaped = content
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
          .replace(/\n/g, '<br>')
        escaped = renderLatex(escaped)
        return DOMPurify.sanitize(escaped, {
          ALLOWED_TAGS: ['br', ...katexTags],
          ADD_ATTR: katexAttrs
        })
      }
    },

    detectHtmlTags(text) {
      if (!text) return false
      const htmlPattern = /<\/?([a-z][a-z0-9]*)\b[^>]*>/i
      return htmlPattern.test(text)
    },

    escapeSingleTildes(text) {
      if (!text) return text
      // Escape single ~ that are not part of ~~ to prevent GFM strikethrough
      // e.g. "~50 nm" should not trigger strikethrough
      return text.replace(/(?<!~)~(?!~)/g, '\\~')
    },

    detectMarkdownPatterns(text) {
      if (!text) return false

      const patterns = [
        /^#{1,6}\s+/m,
        /\*\*[^*]+\*\*/,
        /\*[^*\n]+\*/,
        /\[([^\]]+)\]\(([^)]+)\)/,
        /^[\*\-\+]\s+/m,
        /^\d+\.\s+/m,
        /^>\s+/m,
        /`[^`]+`/,
        /```[\s\S]*?```/,
        /!\[([^\]]*)\]\(([^)]+)\)/,
        /^---+$/m,
        /\~\~[^~]+\~\~/,
        /\^\^[^^]+\^\^/,
        /==[^=]+\==/
      ]

      return patterns.some(pattern => pattern.test(text))
    },

    checkPaywallAccess(groupIndex, postIndex) {
      // Owner always has access
      if (this.effectiveUserId === this.collection.user_id) return { access: true }
      // Subscriber has access to everything
      if (this.$store.state.platformSubscription?.active) return { access: true }
      // Collection-level paywall
      if (this.collection.subscribers_only) return { access: false, level: 'collection' }
      const group = this.collection.groups?.[groupIndex]
      // Group-level paywall
      if (group?.subscribers_only) return { access: false, level: 'group' }
      // Post-level paywall
      const post = group?.knowits?.[postIndex]
      if (post?.subscribers_only) return { access: false, level: 'post' }
      return { access: true }
    },

    toggleAllGroups() {
      if (!this.$refs.tree?.store?.nodesMap) return
      this.allGroupsExpanded = !this.allGroupsExpanded
      Object.keys(this.$refs.tree.store.nodesMap).forEach(key => {
        if (key.startsWith('group-')) {
          this.$refs.tree.store.nodesMap[key].expanded = this.allGroupsExpanded
        }
      })
    },
    expandCurrentGroup() {
      this.$nextTick(() => {
        if (!this.$refs.tree?.store?.nodesMap) return
        
        const currentGroupKey = `group-${this.currentGroupIndex}`
        // First collapse all groups
        Object.keys(this.$refs.tree.store.nodesMap).forEach(key => {
          if (key.startsWith('group-')) {
            this.$refs.tree.store.nodesMap[key].expanded = false
          }
        })
        // Then expand current group
        if (this.$refs.tree.store.nodesMap[currentGroupKey]) {
          this.$refs.tree.store.nodesMap[currentGroupKey].expanded = true
        }
        // Sync background highlight with current node
        this.$refs.tree.setCurrentKey(this.currentCompoundIndex)
      })
    },

    prevAnswer() {
      // Reset scroll position
      this.resetAnswerScroll()
      
      this.selectedAnswer = null
      this.showAnswer = false
      if (this.currentAnswerIndex > 0) {
        this.currentAnswerIndex--
        this.trackAnswerView()
      } else {
        if (this.currentPostIndex > 0) {
          const newPostIndex = this.currentPostIndex - 1
          const newPostId = this.currentGroup.knowits[newPostIndex].knowit_id
          
          this.getPostAnswers(newPostId, this.currentGroupIndex, newPostIndex)
            .then(() => {
              this.currentPostIndex = newPostIndex
              this.currentPostId = newPostId
              this.currentAnswerIndex = this.currentPost.post_info.answers.length - 1
              this.currentCompoundIndex = `${this.currentGroupIndex}-${newPostIndex}`
              this.expandCurrentGroup()
              this.trackAnswerView()
            })
            .catch(error => {
              console.error('Error Loading contents:', error)
              this.$message.error('Failed to load contents')
            })
        } else if (this.currentGroupIndex > 0) {
          const newGroupIndex = this.currentGroupIndex - 1
          const newPostIndex = this.collection.groups[newGroupIndex].knowits.length - 1
          const newPostId = this.collection.groups[newGroupIndex].knowits[newPostIndex].knowit_id
          
          if (!this.collection.groups[newGroupIndex].knowits[newPostIndex].post_info.answers) {
            this.getPostAnswers(newPostId, newGroupIndex, newPostIndex)
              .then(() => {
                this.currentGroupIndex = newGroupIndex
                this.currentPostIndex = newPostIndex
                this.currentPostId = newPostId
                this.currentAnswerIndex = this.currentPost.post_info.answers.length - 1
                this.currentCompoundIndex = `${newGroupIndex}-${newPostIndex}`
                this.expandCurrentGroup()
                this.trackAnswerView()
              })
              .catch(error => {
                console.error('Error Loading contents:', error)
                this.$message.error('Failed to load contents')
              })
          }else{
            this.currentGroupIndex = newGroupIndex
            this.currentPostIndex = newPostIndex
            this.currentPostId = newPostId
            this.currentAnswerIndex = this.currentPost.post_info.answers.length - 1
            this.currentCompoundIndex = `${newGroupIndex}-${newPostIndex}`
            this.expandCurrentGroup()
            this.trackAnswerView()
          }
        } else {
          this.$message({
            message: 'This is the first content of the course',
            type: 'info'
          })
        }
      }
    },

    nextAnswer() {
      // Reset scroll position
      this.resetAnswerScroll()
      
      this.selectedAnswer = null
      this.showAnswer = false
      if (this.currentAnswerIndex < (this.currentPost?.post_info?.answers?.length - 1)) {
        this.currentAnswerIndex++
        this.trackAnswerView()
      } else {
        const nextPostExists = this.currentPostIndex < (this.currentGroup?.knowits?.length - 1)
        const nextGroupExists = this.currentGroupIndex < (this.collection?.groups?.length - 1)

        if (nextPostExists) {
          const newPostIndex = this.currentPostIndex + 1
          const newPostId = this.currentGroup.knowits[newPostIndex].knowit_id
          
          this.getPostAnswers(newPostId, this.currentGroupIndex, newPostIndex)
            .then(() => {
              this.currentPostIndex = newPostIndex
              this.currentPostId = newPostId
              this.currentAnswerIndex = 0
              this.currentCompoundIndex = `${this.currentGroupIndex}-${newPostIndex}`
              this.expandCurrentGroup()
              this.trackAnswerView()
            })
            .catch(error => {
              console.error('Error Loading contents:', error)
              this.$message.error('Failed to load contents')
            })
        } else if (nextGroupExists) {
          const newGroupIndex = this.currentGroupIndex + 1
          const newPostIndex = 0
          const newPostId = this.collection.groups[newGroupIndex].knowits[newPostIndex].knowit_id
          
          this.getPostAnswers(newPostId, newGroupIndex, newPostIndex)
            .then(() => {
              this.currentGroupIndex = newGroupIndex
              this.currentPostIndex = newPostIndex
              this.currentPostId = newPostId
              this.currentAnswerIndex = 0
              this.currentCompoundIndex = `${newGroupIndex}-${newPostIndex}`
              this.expandCurrentGroup()
              this.trackAnswerView()
            })
            .catch(error => {
              console.error('Error Loading contents:', error)
              this.$message.error('Failed to load contents')
            })
        } else {
          this.$message({
            message: 'This is the last content of the course',
            type: 'info'
          })
        }
      }
    },

    goToAnswer(index) {
      // Reset scroll position
      this.resetAnswerScroll()
      
      this.currentAnswerIndex = index
      this.trackAnswerView()
    },

          handleNodeClick(data) {
        if (data.type === 'post') {

          // Check paywall restrictions
          const paywallAccess = this.checkPaywallAccess(data.groupIndex, data.knowitIndex)
          if (!paywallAccess.access) {
            // Navigate to the node but the content area will show paywall overlay
            this.currentGroupIndex = data.groupIndex
            this.currentPostIndex = data.knowitIndex
            this.currentPostId = data.id
            this.currentCompoundIndex = data.compound_index
            this.currentAnswerIndex = 0
            return;
          }

          if(!this.collection.groups[data.groupIndex].knowits[data.knowitIndex].post_info.answers){
          this.getPostAnswers(data.id, data.groupIndex, data.knowitIndex)
            .then(() => {
              this.currentGroupIndex = data.groupIndex
              this.currentPostIndex = data.knowitIndex
              this.currentPostId = data.id
              this.currentCompoundIndex = data.compound_index
              this.currentAnswerIndex = 0
              this.selectedAnswer = null
              this.showAnswer = false
              this.expandCurrentGroup()
              this.trackAnswerView()
            })
            .catch(error => {
              console.error('Error Loading contents:', error)
              this.$message.error('Failed to load contents')
            })
        }else{
          this.currentGroupIndex = data.groupIndex
          this.currentPostIndex = data.knowitIndex
          this.currentPostId = data.id
          this.currentCompoundIndex = data.compound_index
          this.currentAnswerIndex = 0
          this.selectedAnswer = null
          this.showAnswer = false
          this.expandCurrentGroup()
          this.trackAnswerView()
        }

      }
    },

    handleKeyNavigation(event) {
      if (this.isEditingNote) return;
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'INPUT')) return;
      
      // Left/Right arrows for card navigation
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prevAnswer();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.nextAnswer();
      } 
      // Up/Down arrows for content scrolling
      else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const answerContent = document.querySelector('.answer-content');
        if (answerContent) {
          const scrollAmount = 100;
          if (event.key === 'ArrowUp') {
            answerContent.scrollTop -= scrollAmount;
          } else {
            answerContent.scrollTop += scrollAmount;
          }
          event.preventDefault();
        }
      }
    },

    handleMouseDown(event) {
      // Check if we're clicking inside a quiz option or button
      if (event.target.closest('.quiz-option') || event.target.closest('.el-button')) {
        event.stopPropagation()
        return
      }
      
      // Store the initial mouse position and time for detecting drag and double-click
      this.mouseDownPos = {
        x: event.clientX,
        y: event.clientY
      }
      this.mouseDownTime = Date.now()
    },

    handleMouseUp(event) {
      // Check if we're clicking inside a quiz option or button
      if (event.target.closest('.quiz-option') || event.target.closest('.el-button')) {
        event.stopPropagation()
        return
      }
      
      // Check for double-click (within 300ms of last click)
      const currentTime = Date.now()
      const timeSinceLastClick = currentTime - this.lastClickTime
      if (timeSinceLastClick < 300) {
        // Double-click detected, don't navigate
        this.lastClickTime = 0 // Reset to prevent triple-click being treated as another double-click
        return
      }
      this.lastClickTime = currentTime
      
      // Check if this was a drag (attempt to select text) by comparing positions
      if (this.mouseDownPos) {
        const moveX = Math.abs(event.clientX - this.mouseDownPos.x)
        const moveY = Math.abs(event.clientY - this.mouseDownPos.y)
        
        // If the mouse has moved more than 3 pixels in any direction, consider it a drag
        if (moveX > 3 || moveY > 3) {
          return
        }
      }
      
      // Check if there's any text selection
      const selection = window.getSelection()
      if (selection && selection.toString().length > 0) {
        return
      }
      
      // If it was a simple click (not a drag or double-click), handle navigation
      if (event.button === 0) { // Left click
        this.nextAnswer()
      } else if (event.button === 2) { // Right click
        this.prevAnswer()
      }
    },

    handleContextMenu(event) {
      // Prevent default context menu
      event.preventDefault()
    },

    async processAuthCode() {
      try {
        const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id || this.$route.query.id
        const response = await postRequest('validateAuthCode', {
          auth_code: this.authCode,
          collection_id: collectionId
        })
        
        if (response.data.code === 0) {
          this.tempUserId = response.data.data;
          this.effectiveUserId = this.tempUserId;
          // Set temporary user session or handle as needed
          this.fetchCollectionData()
        } else {
          this.$message.error('Invalid authentication code')
          // Redirect to login or collection page
          const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id || this.$route.query.id
          this.$store.dispatch('setTempCollectionId', collectionId)
          this.$router.push({ path: '/deck', query: { id: collectionId } })
        }
      } catch (error) {
        console.error('Auth code validation failed:', error)
        this.$message.error('Authentication failed')
        const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id || this.$route.query.id
        this.$store.dispatch('setTempCollectionId', collectionId)
        this.$router.push({ path: '/deck', query: { id: collectionId } })
      }
    },

    async fetchCollectionData() {
      try {
        console.log('fetchCollectionData called')
        this.$loading({
          lock: true,
          text: 'Loading course...',
          spinner: 'el-icon-loading',
          background: 'rgba(255, 255, 255, 0.7)'
        })

        const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id || this.$route.query.id
        console.log('Collection ID:', collectionId)
        console.log('Effective User ID:', this.effectiveUserId)
        
        if (!collectionId) {
          this.$message.error('Course ID not found')
          this.$router.go(-1)
          return
        }
        
        const params = {
          collection_id: collectionId,
          user_id: this.effectiveUserId
        }
        console.log('API params:', params)

        // Fetch platform subscription if not loaded
        if (!this.$store.state.platformSubscription && this.$store.state.user_id) {
          await this.$store.dispatch('fetchPlatformSubscription')
        }

        const response = await postRequest('getCollectionToRead', params)
        console.log('API response:', response)

        if (response.data.code === 0 && response.data.data) {
          this.collection = response.data.data

          // Check subscription only for collection-level paywall
          const isOwner = this.collection.user_id === this.effectiveUserId
          const hasSubscription = this.$store.state.platformSubscription?.active
          if (!isOwner && !hasSubscription && this.collection.subscribers_only) {
            this.$loading().close()
            this.$confirm(
              'An active platform subscription is required to view this content. Would you like to subscribe now?',
              'Subscription Required',
              {
                confirmButtonText: 'Subscribe Now',
                cancelButtonText: 'Go Back',
                type: 'warning'
              }
            ).then(() => {
              this.$router.push('/platform-subscription')
            }).catch(() => {
              this.$router.go(-1)
            })
            return
          }
          console.log('Collection loaded:', this.collection)
          console.log('Collection groups:', this.collection.groups)
          
          // Initialize view_info and starred_answers/quiz_answers as Sets for each post
          if (this.collection.groups) {
            this.collection.groups.forEach(group => {
              if (group.knowits) {
                group.knowits.forEach(knowit => {
                  if (!knowit.post_info) {
                    this.$set(knowit, 'post_info', {})
                  }
                  if (!knowit.post_info.view_info) {
                    this.$set(knowit.post_info, 'view_info', {})
                  }
                  
                  // Initialize starred_answers as Set
                  const starredAnswers = knowit.post_info.view_info.starred_answers || {}
                  this.$set(knowit.post_info.view_info, 'starred_answers', starredAnswers)  
                  
                  // Initialize quiz_answers as Set
                  const quizAnswers = knowit.post_info.view_info.quiz_answers || {}
                  this.$set(knowit.post_info.view_info, 'quiz_answers', quizAnswers)

                  // Initialize card star status
                  const postId = knowit.post_info._id?.$oid || knowit.post_info._id?.$id
                  if (postId && knowit.post_info.is_starred !== undefined) {
                    this.$set(this.cardStarStatus, postId, knowit.post_info.is_starred)
                  }
                  // knowit.post_info = this.addNotesToPostAnswerFlow(knowit.post_info)
                })
              }
            })
          }
          
          // Process Book Mark - Add safety checks
          if (this.collection.groups && this.collection.groups.length > 0) {
            if (this.collection.collection_view && this.collection.collection_view.mark_post_id) {
              const currentPostId = this.collection.collection_view.mark_post_id

              // Find the group and post indices for the current post ID
              let foundIndices = { groupIndex: 0, postIndex: 0 }
              
              // Safely search for the post
              for (let groupIdx = 0; groupIdx < this.collection.groups.length; groupIdx++) {
                const group = this.collection.groups[groupIdx]
                if (group.knowits && group.knowits.length) {
                  const postIdx = group.knowits.findIndex(k => k.knowit_id === currentPostId)
                  if (postIdx !== -1) {
                    foundIndices = { groupIndex: groupIdx, postIndex: postIdx }
                    break
                  }
                }
              }

              // Check paywall before loading content (consistent with handleNodeClick)
              const bookmarkPaywall = this.checkPaywallAccess(foundIndices.groupIndex, foundIndices.postIndex)
              if (!bookmarkPaywall.access) {
                this.currentPostId = currentPostId
                this.currentGroupIndex = foundIndices.groupIndex
                this.currentPostIndex = foundIndices.postIndex
                this.currentCompoundIndex = `${foundIndices.groupIndex}-${foundIndices.postIndex}`
                this.currentAnswerIndex = 0
                this.$nextTick(() => {
                  this.expandCurrentGroup()
                })
              } else {
                this.getPostAnswers(currentPostId, foundIndices.groupIndex, foundIndices.postIndex)
                  .then(() => {
                    this.currentPostId = currentPostId
                    this.currentGroupIndex = foundIndices.groupIndex
                    this.currentPostIndex = foundIndices.postIndex
                    this.currentCompoundIndex = `${foundIndices.groupIndex}-${foundIndices.postIndex}`

                    if (this.collection.collection_view.mark_answer_uniqueCode) {
                      const currentPost = this.collection.groups[this.currentGroupIndex]?.knowits[this.currentPostIndex]
                      if (currentPost?.post_info?.answers) {
                        this.currentAnswerIndex = currentPost.post_info.answers.findIndex(
                          answer => answer.uniqueCode === this.collection.collection_view.mark_answer_uniqueCode
                        ) || 0
                      }
                    }

                    this.$message({
                      message: 'Resuming from where you left off',
                      type: 'info',
                      duration: 3000
                    })

                    this.$nextTick(() => {
                      this.expandCurrentGroup()
                      this.trackAnswerView()
                    })
                  })
                  .catch(error => {
                    console.error('Error Loading contents:', error)
                    this.$message.error('Failed to load contents')
                  })
              }
            } else {
              // Default to first post if no bookmark

              const firstGroup = this.collection.groups[0]
              if (firstGroup.knowits && firstGroup.knowits.length > 0) {

                // Check paywall before loading content (consistent with handleNodeClick)
                const firstPostPaywall = this.checkPaywallAccess(0, 0)
                if (!firstPostPaywall.access) {
                  this.currentPostId = firstGroup.knowits[0].knowit_id
                  this.currentGroupIndex = 0
                  this.currentPostIndex = 0
                  this.currentAnswerIndex = 0
                  this.$nextTick(() => {
                    this.expandCurrentGroup()
                  })
                } else {
                  this.getPostAnswers(firstGroup.knowits[0].knowit_id,0,0).then(() => {
                    this.currentPostId = firstGroup.knowits[0].knowit_id
                    this.currentGroupIndex = 0
                    this.currentPostIndex = 0
                    this.currentAnswerIndex = 0

                    this.$nextTick(() => {
                      this.expandCurrentGroup()
                      this.trackAnswerView()
                    })
                  })
                  .catch(error => {
                    console.error('Error Loading contents:', error)
                    this.$message.error('Failed to load contents')
                  })
                }

              }
            }
            
            
          }
        } else if (response.data.code === -1) {
          const collectionId = this.$store.state.tempCollectionId || this.$route.query.collection_id || this.$route.query.id
          this.$store.dispatch('setTempCollectionId', collectionId)
          this.$router.push({ path: '/deck', query: { id: collectionId } })
        } else {
          this.$message.error(response.data.msg || 'Failed to load course')
        }
      } catch (error) {
        console.error('Failed to load collection:', error)
        this.$message.error('Failed to load course: ' + error.message)
      } finally {
        this.$loading().close()
      }

      // Fetch starred posts status after collection is loaded
      await this.fetchStarStatus()
    },

    async fetchStarStatus() {
      if (!this.effectiveUserId) return

      try {
        const response = await postRequest('getStarPosts', {
          user_id: this.effectiveUserId
        })

        if (response.data.code === 0) {
          const starredPosts = response.data.data || []
          const starredMap = {}
          starredPosts.forEach(post => {
            if (post?.id) {
              starredMap[post.id] = true
            }
          })
          this.starredPostIds = starredMap
          // Update cardStarStatus for all posts in the collection
          if (this.collection.groups) {
            this.collection.groups.forEach(group => {
              if (group.knowits) {
                group.knowits.forEach(knowit => {
                  const postId = knowit.post_info?._id?.$oid || knowit.post_info?._id?.$id || knowit.knowit_id
                  if (postId) {
                    const isStarred = !!starredMap[postId]
                    this.$set(this.cardStarStatus, postId, isStarred)
                  }
                })
              }
            })
          }
        }
      } catch (error) {
        console.error('Failed to fetch star status:', error)
      }
    },

    getPostAnswers(postId, groupIndex, postIndex) {
      this.$loading({
        lock: true,
        text: 'Loading contents...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      return postRequest('getPostAnswers', {
        user_id:this.effectiveUserId,
        post_id: postId
      })
      .then(async response => {
        const answers = response.data.data

        this.$set(this.collection.groups[groupIndex].knowits[postIndex].post_info, 'answers', answers)

        const postInfo = this.collection.groups[groupIndex].knowits[postIndex].post_info

        // Add ending card
        await this.addEndingCard(postInfo)

        this.$set(this.collection.groups[groupIndex].knowits[postIndex], 'post_info', postInfo)
        const loadedPostId = postInfo?._id?.$oid || postInfo?._id?.$id || postId
        if (loadedPostId) {
          this.$set(this.cardStarStatus, loadedPostId, !!this.starredPostIds[loadedPostId])
        }
      })
      .finally(() => {
        this.$loading().close()
      })
    },

    async trackAnswerView() {
      try {
        // Only track view if user is not the creator (or in reader mode) and we have valid post info and effectiveUserId
        if (this.effectiveUserId &&
            (this.effectiveUserId !== this.collection.user_id || this.isInReaderMode) &&
            (this.currentPost?.post_info?._id?.$oid || this.currentPost?.post_info?._id?.$id)) {
          await postRequest('updatePostView', {
            user_id: this.effectiveUserId,
            post_id: (this.currentPost.post_info._id.$oid || this.currentPost.post_info._id.$id),
            read_scene: 'collection',
            view_method:'COLLECTION',
            read_answer_index: this.currentAnswerIndex,
            answer_uniqueCode: this.currentPost.post_info.answers[this.currentAnswerIndex].uniqueCode
          })
        }
      } catch (error) {
        console.error('Failed to track view:', error)
      }
    },

    isQuizAnswered(uniqueCode) {
      return !!(this.currentPost?.post_info?.view_info?.quiz_answers?.[uniqueCode] ||
               this.localQuizAnswers[uniqueCode])
    },

    getQuizAnswer(uniqueCode) {
      return this.currentPost?.post_info?.view_info?.quiz_answers?.[uniqueCode] ||
             this.localQuizAnswers[uniqueCode] || null
    },

    selectAnswer(idx) {
      event.stopPropagation()
      this.selectedAnswer = idx

      // Instantly record the answer locally for immediate visual feedback
      const uniqueCode = this.currentAnswer?.uniqueCode
      if (uniqueCode && !this.isQuizAnswered(uniqueCode)) {
        const answer = ['A','B','C','D'][idx]
        this.$set(this.localQuizAnswers, uniqueCode, { answer: answer })
        // Fire API in background — don't wait for response
        this.checkAnswer()
      }
    },

    async uploadQuizAnswer(postId, userId, uniqueCode, answer) {
      const response = await postRequest('uploadQuizAnswer', {
        post_id: postId,
        user_id: userId,
        uniqueCode: uniqueCode,
        answer: answer
      })
      
      return response;
    },

    checkAnswer() {
      // Fire API in background — visual feedback is already shown via localQuizAnswers
      if (this.effectiveUserId &&
          (this.effectiveUserId !== this.collection.user_id || this.isInReaderMode) &&
          this.selectedAnswer !== null) {
        const postId = this.currentPost.post_info._id.$oid || this.currentPost.post_info._id.$id
        const uniqueCode = this.currentPost.post_info.answers[this.currentAnswerIndex].uniqueCode
        const answer = ['A','B','C','D'][this.selectedAnswer]

        this.uploadQuizAnswer(postId, this.effectiveUserId, uniqueCode, answer)
          .then(result => {
            if (result.data.code === 0) {
              // Sync server data back for consistency
              this.currentPost.post_info.view_info = result.data.data
            } else {
              this.$message.warning('Failed to save answer: ' + (result.data.msg || 'Unknown error'))
            }
          })
          .catch(error => {
            console.error('Failed to submit answer:', error)
            this.$message.error('Failed to save your answer to server. Your answer is still recorded locally.')
          })
      }
    },

    // ── Quiz type helpers ──────────────────────────────────────────────
    getQuizType(answer) {
      return answer?.quiz_type || 'multiple_choice'
    },

    resetQuizTypeState() {
      const answer = this.currentAnswer
      const quizType = this.getQuizType(answer)
      // Reset all quiz-type states
      this.scSelections = []
      this.scActiveBlank = null
      this.scChecked = false
      this.spLetters = []
      this.spChecked = false
      this.matchPairs = {}
      this.matchActiveLeft = null
      this.matchChecked = false

      if (quizType === 'sentence_completion' && answer?.quiz_content) {
        const blanks = (answer.quiz_content.question || '').split('[BLANK]')
        const blankCount = blanks.length - 1
        this.scSelections = new Array(blankCount).fill(null)
        const saved = this.getQuizAnswer(answer.uniqueCode)
        if (saved) {
          try {
            const pairs = JSON.parse(saved.answer)
            pairs.forEach(([bi, oi]) => { if (bi < blankCount) this.$set(this.scSelections, bi, oi) })
            this.scChecked = true
          } catch {}
        }
      } else if (quizType === 'word_spelling' && answer?.quiz_content) {
        const correctAnswer = answer.quiz_content.correct_answer || ''
        const hintSet = new Set(answer.quiz_content.hint_indexes || [])
        this.spHintLevel = 0
        this.spLetters = Array.from({ length: correctAnswer.length }, (_, i) =>
          hintSet.has(i) ? correctAnswer[i] : ''
        )
        const saved = this.getQuizAnswer(answer.uniqueCode)
        if (saved) {
          this.spLetters = saved.answer.split('')
          this.spChecked = true
        }
      } else if (quizType === 'matching' && answer?.quiz_content) {
        this.matchPairs = {}
        const saved = this.getQuizAnswer(answer.uniqueCode)
        if (saved) {
          try {
            const pairs = JSON.parse(saved.answer)
            pairs.forEach(([li, ri]) => { this.$set(this.matchPairs, li, ri) })
            this.matchChecked = true
          } catch {}
        }
      }
    },

    submitGenericQuizAnswer(answer) {
      const uniqueCode = this.currentAnswer?.uniqueCode
      if (!uniqueCode) return
      this.$set(this.localQuizAnswers, uniqueCode, { answer })
      if (this.effectiveUserId &&
          (this.effectiveUserId !== this.collection.user_id || this.isInReaderMode)) {
        const postId = this.currentPost.post_info._id.$oid || this.currentPost.post_info._id.$id
        this.uploadQuizAnswer(postId, this.effectiveUserId, uniqueCode, answer)
          .then(result => {
            if (result.data.code === 0) {
              this.currentPost.post_info.view_info = result.data.data
            }
          })
          .catch(error => { console.error('Failed to submit answer:', error) })
      }
    },

    // ── Sentence Completion ─────────────────────────────────────────────
    scSelectOption(optIdx) {
      if (this.scActiveBlank === null || this.scChecked) return
      this.$set(this.scSelections, this.scActiveBlank, optIdx)
      this.scActiveBlank = null
    },
    scCheckAnswers() {
      if (this.scSelections.some(s => s === null)) return
      const pairs = this.scSelections.map((oi, bi) => [bi, oi])
      this.scChecked = true
      this.submitGenericQuizAnswer(JSON.stringify(pairs))
    },
    scGetBlankResult(bi) {
      if (!this.scChecked) return null
      const ca = this.currentAnswer?.quiz_content?.correct_answer || []
      const correctMap = Object.fromEntries(ca.map(([b, o]) => [b, o]))
      return this.scSelections[bi] === correctMap[bi] ? 'correct' : 'wrong'
    },
    scCorrectCount() {
      const ca = this.currentAnswer?.quiz_content?.correct_answer || []
      const correctMap = Object.fromEntries(ca.map(([b, o]) => [b, o]))
      return this.scSelections.filter((oi, bi) => oi === correctMap[bi]).length
    },
    scBlankCount() {
      return (this.currentAnswer?.quiz_content?.question || '').split('[BLANK]').length - 1
    },
    scBlanks() {
      return (this.currentAnswer?.quiz_content?.question || '').split('[BLANK]')
    },
    scUsedOptions() {
      return new Set(this.scSelections.filter(s => s !== null))
    },

    // ── Word Spelling ───────────────────────────────────────────────────
    spHandleChange(idx, char) {
      if (this.spIsHintOrRevealed(idx) || this.spChecked) return
      this.$set(this.spLetters, idx, char.slice(-1))
    },
    spCheck() {
      const correctAnswer = this.currentAnswer?.quiz_content?.correct_answer || ''
      if (this.spLetters.join('').length !== correctAnswer.length) return
      this.spChecked = true
      this.submitGenericQuizAnswer(this.spLetters.join(''))
    },
    spHandleKeyDown(idx, e) {
      if (e.key === 'Backspace' && !this.spLetters[idx] && idx > 0) {
        const prev = e.target.previousElementSibling
        if (prev) prev.focus()
      }
    },
    spHandleInput(idx, e) {
      if (e.target.value) {
        const next = e.target.nextElementSibling
        if (next && !this.spIsHintOrRevealed(idx + 1)) next.focus()
      }
    },
    spIsCorrect() {
      const correctAnswer = this.currentAnswer?.quiz_content?.correct_answer || ''
      return this.spLetters.join('').toLowerCase() === correctAnswer.toLowerCase()
    },
    spIsHint(idx) {
      return new Set(this.currentAnswer?.quiz_content?.hint_indexes || []).has(idx)
    },
    // Returns the set of indexes revealed by the current hint level
    spHintRevealedIndexes() {
      const ca = this.currentAnswer?.quiz_content?.correct_answer || ''
      const len = ca.length
      if (len === 0) return new Set()
      const revealed = new Set()
      if (this.spHintLevel >= 1) revealed.add(0) // first letter
      if (this.spHintLevel >= 2 && len > 1) revealed.add(len - 1) // last letter
      if (this.spHintLevel >= 3) {
        // Reveal ~half of remaining hidden middle letters, evenly spaced
        const staticHints = new Set(this.currentAnswer?.quiz_content?.hint_indexes || [])
        const hidden = []
        for (let i = 1; i < len - 1; i++) {
          if (!revealed.has(i) && !staticHints.has(i)) hidden.push(i)
        }
        const count = Math.max(1, Math.ceil(hidden.length / 2))
        const step = hidden.length / count
        for (let j = 0; j < count; j++) {
          revealed.add(hidden[Math.floor(j * step)])
        }
      }
      if (this.spHintLevel >= 4) {
        // Reveal all letters (give up)
        for (let i = 0; i < len; i++) revealed.add(i)
      }
      return revealed
    },
    // Checks if index is either a static hint or dynamically revealed
    spIsHintOrRevealed(idx) {
      if (this.spIsHint(idx)) return true
      return this.spHintRevealedIndexes().has(idx)
    },
    // Progressive hint: each click reveals more letters
    spUseHint() {
      if (this.spHintLevel >= 4 || this.spChecked) return
      this.spHintLevel++
      const ca = this.currentAnswer?.quiz_content?.correct_answer || ''
      const revealed = this.spHintRevealedIndexes()
      // Fill in revealed letters
      for (const idx of revealed) {
        this.$set(this.spLetters, idx, ca[idx])
      }
    },
    spLetterResult(idx) {
      if (!this.spChecked) return null
      const ca = this.currentAnswer?.quiz_content?.correct_answer || ''
      return (this.spLetters[idx] || '').toLowerCase() === (ca[idx] || '').toLowerCase() ? 'correct' : 'wrong'
    },

    // ── Matching ────────────────────────────────────────────────────────
    matchSelectLeft(li) {
      if (this.matchChecked) return
      this.matchActiveLeft = li
    },
    matchSelectRight(ri) {
      if (this.matchActiveLeft === null || this.matchChecked) return
      this.$set(this.matchPairs, this.matchActiveLeft, ri)
      this.matchActiveLeft = null
    },
    matchCheck() {
      const leftItems = this.currentAnswer?.quiz_content?.left_items || []
      if (Object.keys(this.matchPairs).length < leftItems.length) return
      const arr = Object.entries(this.matchPairs).map(([li, ri]) => [parseInt(li), ri])
      this.matchChecked = true
      this.submitGenericQuizAnswer(JSON.stringify(arr))
    },
    matchGetPairResult(li) {
      if (!this.matchChecked || this.matchPairs[li] === undefined) return null
      const ca = this.currentAnswer?.quiz_content?.correct_pairs || this.currentAnswer?.quiz_content?.correct_answer || []
      const correctMap = Object.fromEntries(ca.map(([l, r]) => [l, r]))
      return this.matchPairs[li] === correctMap[li] ? 'correct' : 'wrong'
    },
    matchCorrectCount() {
      const ca = this.currentAnswer?.quiz_content?.correct_pairs || this.currentAnswer?.quiz_content?.correct_answer || []
      const correctMap = Object.fromEntries(ca.map(([l, r]) => [l, r]))
      return Object.entries(this.matchPairs).filter(([li, ri]) => correctMap[parseInt(li)] === ri).length
    },
    matchUsedRight() {
      return new Set(Object.values(this.matchPairs))
    },
    matchAllPaired() {
      const leftItems = this.currentAnswer?.quiz_content?.left_items || []
      return Object.keys(this.matchPairs).length >= leftItems.length
    },

    toggleNav() {
      this.showNav = !this.showNav
      // Mark as manually closed/opened to prevent auto-reopen
      this.manuallyClosedNav = !this.showNav
    },

    handleResize() {
      const width = window.innerWidth

      // Auto-collapse left sidebar based on screen width (only left sidebar, AI panel is user-controlled)
      if (width <= 1200) {
        // Close left sidebar on smaller screens
        this.showNav = false
        this.manuallyClosedNav = false // Reset manual flag on auto-collapse
      }

      // Re-open left sidebar when screen is wide enough (only if not manually closed)
      if (width > 1200 && !this.showNav && !this.manuallyClosedNav) {
        this.showNav = true
      }

      // Note: AI panel (showAI) is now fully user-controlled, no auto-collapse/expand

      // Force a re-render
      this.$forceUpdate()
    },

    async toggleStar() {
      if (!this.effectiveUserId ||
          !(this.currentPost?.post_info?._id?.$oid || this.currentPost?.post_info?._id?.$id) ||
          !this.currentAnswer?.uniqueCode) return
      
      try {
        const uniqueCode = this.currentAnswer.uniqueCode
        const response = await postRequest('toggleStarAnswer', {
          user_id: this.effectiveUserId,
          post_id: (this.currentPost.post_info._id.$oid || this.currentPost.post_info._id.$id),
          uniqueCode: uniqueCode,
          is_starred: !this.currentPost.post_info?.view_info?.starred_answers?.[uniqueCode]
        })
        
        if (response.data.code === 0) {
          if (!this.currentPost.post_info.view_info) {
            this.$set(this.currentPost.post_info, 'view_info', {})
          }
          if (!(this.currentPost.post_info.view_info.starred_answers instanceof Object)) {
            this.$set(this.currentPost.post_info.view_info, 'starred_answers', {})
          }
          
          if (this.currentPost.post_info.view_info.starred_answers[uniqueCode]) {
            this.$delete(this.currentPost.post_info.view_info.starred_answers, uniqueCode)
            this.$message.success('Card unstarred')
          } else {
            this.$set(this.currentPost.post_info.view_info.starred_answers, uniqueCode, new Date().getTime()/1000)
            this.$message.success('Card starred')
          }
        } else {
          this.$message.error(response.data.msg || 'Failed to update star status')
        }
      } catch (error) {
        console.error('Failed to toggle star:', error)
        this.$message.error('Failed to update star status')
      }
    },
    async toggleCardStar() {
      if (!this.effectiveUserId || !this.currentPost?.post_info) return

      const postId = this.currentPost.post_info._id?.$oid || this.currentPost.post_info._id?.$id
      if (!postId) return

      console.log('toggleCardStar called:', { user_id: this.effectiveUserId, post_id: postId })

      try {
        const response = await postRequest('StarPost', {
          user_id: this.effectiveUserId,
          post_id: postId
        })

        console.log('StarPost response:', response.data)

        if (response.data.code === 0) {
          const isStarred = response.data.data.is_starred
          this.$set(this.cardStarStatus, postId, isStarred)
          if (isStarred) {
            this.$set(this.starredPostIds, postId, true)
          } else {
            this.$delete(this.starredPostIds, postId)
          }
          this.$message.success(isStarred ? 'Card starred successfully' : 'Card unstarred successfully')
        } else {
          this.$message.error(response.data.msg || 'Failed to update star status')
        }
      } catch (error) {
        console.error('Failed to toggle card star:', error)
        this.$message.error('Failed to update star status')
      }
    },
    getCardStarStatus(postId) {
      if (postId in this.cardStarStatus) {
        return this.cardStarStatus[postId]
      }
      if (postId && this.starredPostIds[postId]) {
        return true
      }
      return this.currentPost?.post_info?.is_starred || false
    },
    toggleAI() {
      this.showAI = !this.showAI
      if (this.showAI) {
        this.showNotes = false
      }
    },
    toggleNotes() {
      this.showNotes = !this.showNotes
      if (this.showNotes) {
        this.showAI = false
        this.sidebarNoteContent = ''
        this.editingSidebarNoteIndex = null
      }
    },
    saveSidebarNote() {
      if (!this.sidebarNoteContent.trim()) return
      if (!this.effectiveUserId) {
        this.$message.error('User ID is required to save note')
        return
      }
      const uniqueCode = this.currentAnswer?.uniqueCode
      if (!uniqueCode) {
        this.$message.warning('Please navigate to a card first')
        return
      }
      const postId = this.currentPost.post_info._id.$oid || this.currentPost.post_info._id.$id
      const noteData = {
        content_type: 'note',
        note_type: 'text',
        post_id: postId,
        uniqueCode: uniqueCode,
        note_content: this.sidebarNoteContent,
        quote_content: '',
      }
      if (this.editingSidebarNoteIndex !== null) {
        noteData.index = this.editingSidebarNoteIndex
      }
      postRequest('saveNote', { editNote: noteData, user_id: this.effectiveUserId })
        .then(response => {
          if (response.data.code === 0) {
            this.$message.success('Note saved successfully')
            this.sidebarNoteContent = ''
            this.editingSidebarNoteIndex = null
            this.refreshPostNotes(response.data.data)
          } else {
            this.$message.error(response.data.msg || 'Failed to save note')
          }
        })
        .catch(() => this.$message.error('Failed to save note'))
    },
    editSidebarNote(idx) {
      const note = this.currentCardNotes[idx]
      if (!note) return
      this.sidebarNoteContent = note.note_content || ''
      this.editingSidebarNoteIndex = idx
    },
    cancelEditSidebarNote() {
      this.sidebarNoteContent = ''
      this.editingSidebarNoteIndex = null
    },
    deleteSidebarNote(idx) {
      if (!this.effectiveUserId) {
        this.$message.error('User ID is required')
        return
      }
      const uniqueCode = this.currentAnswer?.uniqueCode
      const postId = this.currentPost.post_info._id.$oid || this.currentPost.post_info._id.$id
      this.$confirm('Are you sure you want to delete this note?', 'Warning', {
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        type: 'warning'
      }).then(() => {
        postRequest('deleteNote', {
          user_id: this.effectiveUserId,
          post_id: postId,
          unique_code: uniqueCode,
          index: idx
        }).then(response => {
          if (response.data.code === 0) {
            this.$message.success('Note deleted successfully')
            if (this.editingSidebarNoteIndex === idx) {
              this.sidebarNoteContent = ''
              this.editingSidebarNoteIndex = null
            }
            this.refreshPostNotes(response.data.data)
          } else {
            this.$message.error(response.data.msg || 'Failed to delete note')
          }
        }).catch(() => this.$message.error('Failed to delete note'))
      }).catch(() => {})
    },
    refreshPostNotes(newViewData) {
      this.currentPost.post_info.view_info = newViewData
      this.$forceUpdate()
    },
    addNote(answer,index=null) {
      let quoteContent = ''
      if (index !== null) {
        quoteContent = answer.quote_content || ''
      } else if (answer.content_type === 'quiz' && answer.quiz_content) {
        quoteContent = this.renderMarkdownContent(answer.quiz_content.question || '')
      } else if (answer.content_type === 'text' && answer.content) {
        quoteContent = this.renderAnswerContent(answer)
      } else {
        quoteContent = answer.content || ''
      }

      if (index !== null) {
        this.editNote = {
          content_type:"note",
          note_type:"text",
          post_id: answer.post_id,
          uniqueCode: answer.uniqueCode,
          note_content: answer.note_content,
          quote_content: quoteContent,
        }
        this.editNote.index = index
      } else {
        this.editNote = {
          content_type:"note",
          note_type:"text",
          post_id: (this.currentPost.post_info._id.$oid || this.currentPost.post_info._id.$id),
          uniqueCode: answer.uniqueCode,
          note_content: '',
          quote_content: quoteContent,
        }
      }
      this.isEditingNote = true
    },
    saveNote(){
      if (!this.effectiveUserId) {
        this.$message.error('User ID is required to save note')
        return
      }
      
      console.log(this.editNote)
      var params = {
        'editNote': this.editNote,
        'user_id': this.effectiveUserId
      }
      postRequest('saveNote', params)
        .then(response => {
          if (response.data.code === 0) {
            this.$message.success('Note saved successfully')
            this.isEditingNote = false

            this.currentPost.post_info.view_info = response.data.data
            this.$forceUpdate()
          } else {
            this.$message.error(response.data.msg || 'Failed to save note')
          }
        })
        
    } ,
    addNotesToPostAnswerFlow(post_info){
      if (post_info.view_info.note_array && Object.keys(post_info.view_info.note_array).length > 0) {
        var new_answers = post_info.answers
        Object.entries(post_info.view_info.note_array).forEach(([key, note_item_array]) => {

          var answerIndex = new_answers.findIndex(answer => answer.uniqueCode === key);


          note_item_array.forEach((note, n) => {
            const noteAnswer = {
              content_type: "note",
              note_content:note.note_content,
              quote_content:note.quote_content,
              has_titles:false,
              is_hidden:false,
              uniqueCode: key,
              note_index:n,
              post_id:(post_info._id.$oid || post_info._id.$id),
              latest_update_time:note.latest_update_time || null
            }
            answerIndex ++
            new_answers.splice(answerIndex, 0, noteAnswer)
          })
        })
        post_info.answers = new_answers
      }

      return post_info
    },

    async addEndingCard(post_info) {
      // Check if content_summary exists and is not empty
      let contentSummary = null
      if (post_info.content_summary && post_info.content_summary.trim()) {
        contentSummary = post_info.content_summary
      }

      const endingCard = {
        content_type: 'ending',
        content: contentSummary ? this.generateSummaryView(contentSummary) : this.generateSimpleEndingView(),
        is_ending_card: true
      }
      post_info.answers.push(endingCard)
    },

    generateSimpleEndingView() {
      return `
        <div style="text-align: center; padding: 60px 20px;">
          <div style="width: 72px; height: 72px; margin: 0 auto 24px; background: linear-gradient(135deg, #ecf5ff 0%, #d9ecff 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 36px; line-height: 72px;">🎉</div>
          <h2 style="font-size: 28px; font-weight: 700; margin-bottom: 12px; color: #1a2332; letter-spacing: -0.01em;">Congratulations!</h2>
          <p style="font-size: 16px; line-height: 1.6; color: #4a5568;">You've completed this stack.</p>
        </div>
      `
    },

    generateSummaryView(summary) {
      return `
        <div style="text-align: center; padding: 40px 20px;">
          <div style="width: 72px; height: 72px; margin: 0 auto 24px; background: linear-gradient(135deg, #ecf5ff 0%, #d9ecff 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 36px; line-height: 72px;">🎉</div>
          <h2 style="font-size: 28px; font-weight: 700; margin-bottom: 12px; color: #1a2332; letter-spacing: -0.01em;">Congratulations!</h2>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 32px; color: #4a5568;">You've completed this stack. Here's your takeaway:</p>
          <div style="background: linear-gradient(135deg, #f4f8ff 0%, #ecf5ff 100%); border: 1px solid rgba(64,158,255,0.15); border-radius: 12px; padding: 24px; margin-bottom: 24px; text-align: left;">
            <div style="display: flex; align-items: center; margin-bottom: 12px; color: #409EFF;">
              <span style="font-size: 18px; margin-right: 8px;">📝</span>
              <span style="font-weight: 600; font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em;">Key Takeaways</span>
            </div>
            <div style="color: #4a5568; font-size: 15px; line-height: 1.8; white-space: pre-wrap;">${summary}</div>
          </div>
        </div>
      `
    },


    deleteNote() {

      if (!this.effectiveUserId) {
        this.$message.error('User ID is required to delete note')
        return
      }

      // Show confirmation dialog before deleting
      this.$confirm('Are you sure you want to delete this note?', 'Warning', {
        confirmButtonText: 'Yes',
        cancelButtonText: 'No', 
        type: 'warning'
      }).then(() => {
        const params = {
          user_id: this.effectiveUserId,
          post_id: this.editNote.post_id,
          unique_code: this.editNote.uniqueCode,
          index: this.editNote.index
        }

        postRequest('deleteNote', params)
          .then(response => {
            if (response.data.code === 0) {
              this.$message.success('Note deleted successfully')
              this.isEditingNote = false

              this.currentPost.post_info.view_info = response.data.data
              this.$forceUpdate()
            } else {
              this.$message.error(response.data.msg || 'Failed to delete note')
            }
          })
          .catch(error => {
            console.error('Failed to delete note:', error)
            this.$message.error('Failed to delete note')
          })
    
      })
    },
    // Add new method to reset scroll
    resetAnswerScroll() {
      // Use nextTick to ensure DOM is updated before scrolling
      this.$nextTick(() => {
        const answerContent = document.querySelector('.answer-content')
        if (answerContent) {
          answerContent.scrollTop = 0
        }
      })
    },
    isQuizCompleted(index) {
      if (!this.currentPost?.post_info?.answers) return false
      const quizAnswers = this.currentPost.post_info.view_info?.quiz_answers || {}
      const quizzes = this.currentPost.post_info.answers.filter(a => a.content_type === 'quiz')
      const quiz = quizzes[index]
      if (!quiz) return false
      return !!(quizAnswers[quiz.uniqueCode] || this.localQuizAnswers[quiz.uniqueCode])
    },

    jumpToQuiz(index) {
      if (!this.currentPost?.post_info?.answers) return
      const quizzes = this.currentPost.post_info.answers.filter(a => a.content_type === 'quiz')
      const targetQuiz = quizzes[index]
      if (targetQuiz) {
        const targetIndex = this.currentPost.post_info.answers.findIndex(a => a.uniqueCode === targetQuiz.uniqueCode)
        if (targetIndex !== -1) {
          this.goToAnswer(targetIndex)
        }
      }
    },

    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;

      if (this.isFullScreen) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error('Error attempting to enable full-screen mode:', err);
        });
      } else {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
      }
    },

    // Markdown editor methods
    serializeQuizContent(qc) {
      if (!qc) return ''
      const lines = []
      lines.push('[Question]')
      lines.push(qc.question || '')
      lines.push('')
      const labels = ['A', 'B', 'C', 'D']
      ;(qc.options || []).forEach((opt, i) => {
        if (i < 4) {
          lines.push(`[${labels[i]}]`)
          lines.push(opt || '')
          lines.push('')
        }
      })
      // Determine correct answer letter
      const correctIdx = (qc.options || []).indexOf(qc.correct_answer)
      const correctLetter = correctIdx >= 0 ? labels[correctIdx] : (qc.correct_answer || '')
      lines.push('[Answer]')
      lines.push(correctLetter)
      lines.push('')
      lines.push('[Explanation]')
      lines.push(qc.explanation || '')
      return lines.join('\n')
    },
    parseQuizContent(text) {
      if (!text) return { question: '', options: ['', '', '', ''], correct_answer: '', explanation: '' }
      const sections = {}
      let currentKey = null
      const lines = text.split('\n')
      for (const line of lines) {
        const match = line.match(/^\[(Question|A|B|C|D|Answer|Explanation)\]\s*$/)
        if (match) {
          currentKey = match[1]
          sections[currentKey] = []
        } else if (currentKey) {
          sections[currentKey] = sections[currentKey] || []
          sections[currentKey].push(line)
        }
      }
      const trim = (arr) => (arr || []).join('\n').trim()
      const options = [trim(sections['A']), trim(sections['B']), trim(sections['C']), trim(sections['D'])]
      const answerLetter = trim(sections['Answer']).toUpperCase()
      const answerIdx = ['A', 'B', 'C', 'D'].indexOf(answerLetter)
      const correct_answer = answerIdx >= 0 ? options[answerIdx] : ''
      return {
        question: trim(sections['Question']),
        options,
        correct_answer,
        explanation: trim(sections['Explanation'])
      }
    },
    startEditMarkdown() {
      this.editingMarkdownContent = this.currentCardMarkdown
      this.isEditingMarkdown = true
    },
    cancelEditMarkdown() {
      this.isEditingMarkdown = false
      this.editingMarkdownContent = ''
    },
    async saveMarkdown() {
      if (!this.currentAnswer || !this.currentPost) return
      this.isSavingMarkdown = true
      try {
        const postInfo = this.currentPost.post_info
        const postId = postInfo._id?.$oid || postInfo._id?.$id
        if (!postId) {
          this.$message.error('Post ID not found')
          return
        }

        // Update the answer content locally
        const answerIndex = this.currentAnswerIndex
        const answers = [...postInfo.answers]
        // Skip ending card in the save payload
        const answersToSave = answers.filter(a => a.content_type !== 'ending')

        // Find the actual index in answersToSave and apply changes
        const answer = answers[answerIndex]
        const saveIndex = answersToSave.indexOf(answer)
        const isQuiz = answer.content_type === 'quiz'
        let parsedQuiz = null

        if (saveIndex !== -1) {
          if (isQuiz) {
            parsedQuiz = this.parseQuizContent(this.editingMarkdownContent)
            answersToSave[saveIndex] = { ...answersToSave[saveIndex], quiz_content: parsedQuiz }
          } else {
            answersToSave[saveIndex] = { ...answersToSave[saveIndex], content: this.editingMarkdownContent }
          }
        }

        const response = await postRequest('updatePost', {
          id: postId,
          user_id: this.collection.user_id,
          title: postInfo.title,
          description: postInfo.description || '',
          content_type: postInfo.content_type || 'knowledge',
          answers: answersToSave,
          price: postInfo.price || 0,
          labels: postInfo.labels || [],
          status: postInfo.status || 'PACKAGED',
          usd_price: postInfo.usd_price || 0,
          token_price: postInfo.token_price || 0,
          collection_id: this.$store.state.tempCollectionId || this.$route.query.collection_id || this.$route.query.id || '',
          group_index: this.currentGroupIndex,
          post_index: this.currentPostIndex,
          access_type: 'collection',
          content_summary: postInfo.content_summary || ''
        })

        if (response.data.code === 0) {
          // Update local data
          if (isQuiz && parsedQuiz) {
            this.$set(this.currentPost.post_info.answers[answerIndex], 'quiz_content', parsedQuiz)
          } else {
            this.$set(this.currentPost.post_info.answers[answerIndex], 'content', this.editingMarkdownContent)
          }
          this.isEditingMarkdown = false
          this.editingMarkdownContent = ''
          this.$message.success('保存成功')
        } else {
          this.$message.error(response.data.msg || '保存失败')
        }
      } catch (error) {
        console.error('Failed to save markdown:', error)
        this.$message.error('保存失败')
      } finally {
        this.isSavingMarkdown = false
      }
    },
    insertTab(e) {
      const textarea = e.target
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      this.editingMarkdownContent = this.editingMarkdownContent.substring(0, start) + '  ' + this.editingMarkdownContent.substring(end)
      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2
      })
    },

    toggleViewMode() {
      if (this.viewMode === 'creator') {
        this.viewMode = 'reader'
        this.$message.success('Switched to Reader Mode. You can now answer quizzes and track your progress.')
      } else {
        this.viewMode = 'creator'
        this.$message.success('Switched to Creator Preview Mode. All answers are visible.')
      }
      // Reset selected answer and markdown editing when switching modes
      this.selectedAnswer = null
      this.isEditingMarkdown = false
      this.editingMarkdownContent = ''
    },
  }
}
</script>

<style scoped>
/* Style for images within v-html content */
.text-content /deep/ img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

/* Markdown styles for headings */
.text-content /deep/ h1,
.text-content /deep/ h2,
.text-content /deep/ h3,
.text-content /deep/ h4,
.text-content /deep/ h5,
.text-content /deep/ h6 {
  font-weight: 600;
  line-height: 1.4;
  margin-top: 24px;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.text-content /deep/ h1 {
  font-size: 2em;
  border-bottom: 2px solid rgba(var(--primary-rgb), 0.12);
  padding-bottom: 0.3em;
}

.text-content /deep/ h2 {
  font-size: 1.5em;
  border-bottom: 1px solid rgba(var(--primary-rgb), 0.08);
  padding-bottom: 0.3em;
}

.text-content /deep/ h3 {
  font-size: 1.25em;
}

.text-content /deep/ h4 {
  font-size: 1em;
}

.text-content /deep/ h5 {
  font-size: 0.875em;
}

.text-content /deep/ h6 {
  font-size: 0.85em;
  color: #606266;
}

/* Code blocks */
.text-content /deep/ pre {
  background-color: #f4f8ff;
  border: 1px solid rgba(var(--primary-rgb), 0.1);
  border-radius: 10px;
  padding: 16px;
  overflow-x: auto;
  margin: 16px 0;
}

.text-content /deep/ code {
  background-color: var(--primary-light);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9em;
  color: var(--primary);
}

.text-content /deep/ pre code {
  background-color: transparent;
  padding: 0;
  color: #303133;
  font-size: 14px;
  line-height: 1.6;
}

/* Lists */
.text-content /deep/ ul,
.text-content /deep/ ol {
  padding-left: 2em;
  margin: 12px 0;
}

.text-content /deep/ li {
  margin: 8px 0;
  line-height: 1.6;
}

/* Paragraphs */
.text-content /deep/ p {
  margin: 12px 0;
  line-height: 1.6;
}

/* Blockquotes */
.text-content /deep/ blockquote {
  margin: 16px 0;
  padding: 14px 20px;
  border-left: 4px solid var(--primary);
  background: linear-gradient(135deg, #f4f8ff 0%, var(--primary-light) 100%);
  border-radius: 0 8px 8px 0;
  color: var(--text-secondary);
}

/* Horizontal rules */
.text-content /deep/ hr {
  border: none;
  border-top: 2px solid #ebeef5;
  margin: 24px 0;
}

/* Links */
.text-content /deep/ a {
  color: var(--primary);
  text-decoration: none;
}

.text-content /deep/ a:hover {
  text-decoration: underline;
}

/* Bold and italic */
.text-content /deep/ strong {
  font-weight: 600;
}

.text-content /deep/ em {
  font-style: italic;
}

/* Style for tables within v-html content */
.content-wrapper /deep/ table {
  border-collapse: collapse;
  border: 1px solid #e0e8f4;
  width: 100%;
  margin: 10px 0;
  border-radius: 8px;
  overflow: hidden;
}

.content-wrapper /deep/ table th,
.content-wrapper /deep/ table td {
  border: 1px solid #e0e8f4;
  padding: 9px 14px;
  text-align: left;
}

.content-wrapper /deep/ table th {
  background: linear-gradient(135deg, #f0f6ff 0%, var(--primary-light) 100%);
  font-weight: 600;
  color: var(--text-primary);
}

.content-wrapper /deep/ table td {
  color: var(--text-secondary);
}
.read-container {
  --primary: #1159af;
  --primary-dark: #0d4a96;
  --primary-light: #e8f0fb;
  --primary-hover: #2171c7;
  --primary-rgb: 17, 89, 175;
  --text-primary: #1a2332;
  --text-secondary: #4a5568;
  --text-muted: #8a9ab5;
  --border-color: rgba(64, 158, 255, 0.1);
  --border-subtle: rgba(64, 158, 255, 0.06);
  --bg-page: #f4f7fb;
  --bg-sidebar: #f8fafd;
  --bg-card: #ffffff;
  --shadow-card: 0 4px 24px rgba(64, 158, 255, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
  --shadow-float: 0 2px 16px rgba(64, 158, 255, 0.12), 0 1px 4px rgba(0,0,0,0.06);

  width: 100vw;
  max-width: 100vw;
  display: flex;
  overflow: hidden;
  overflow-x: hidden;
  background: var(--bg-page);
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
}


/* Left Sidebar Styles */
.left-sidebar {
  width: 300px;
  height: 100%;
  background: var(--bg-sidebar);
  border-right: 1px solid var(--border-color);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}


.left-sidebar.collapsed {
  width: 0;
  border-right: none;
  padding: 0;
  overflow: hidden;
}

.sidebar-content-hidden {
  opacity: 0;
  padding: 0;
  width: 0;
  overflow: hidden;
}

/* Sidebar Toggle Button */
.sidebar-toggle-btn {
  position: fixed;
  top: 50%;
  left: 300px;
  transform: translateY(-50%);
  width: 24px;
  height: 48px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-left: none;
  border-radius: 0 8px 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--text-secondary);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
}

.sidebar-toggle-btn:hover {
  background: var(--primary-light);
  color: var(--primary);
}

.sidebar-toggle-btn.sidebar-toggle-btn-collapsed {
  left: 0;
  border-radius: 0 8px 8px 0;
}

/* Auto-collapse left sidebar on smaller screens */
@media (max-width: 1200px) {
  .left-sidebar:not(.force-open) {
    width: 0;
    border-right: none;
  }
  .left-sidebar.force-open {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99;
    height: 100%;
    width: 300px;
    background: var(--bg-sidebar);
    box-shadow: 4px 0 16px rgba(0, 0, 0, 0.1);
  }
}

.sidebar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow: hidden;
  height: 100%;
  min-height: 0;
}

.key-info-section {
  flex-shrink: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f8;
  margin-bottom: 12px;
}

.key-info-section .collection-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: var(--text-primary);
  letter-spacing: 0.01em;
  line-height: 1.4;
}

.key-info-section .collection-subtitle {
  font-size: 13px;
  font-weight: 400;
  margin: 0 0 8px 0;
  color: var(--text-secondary, #666);
  line-height: 1.4;
}

.key-info-section .group-info {
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
}

.key-info-section .post-title {
  font-size: 14px;
  font-weight: 500;
  margin: 8px 0;
  color: var(--text-secondary);
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.quiz-summary {
  margin-top: 12px;
}

.catalog-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.catalog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.catalog-header h3 {
  font-size: 11px;
  font-weight: 700;
  margin: 0;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.toggle-expand-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 14px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.2s, background 0.2s;
}

.toggle-expand-btn:hover {
  color: var(--text-primary);
  background: var(--bg-hover, rgba(0,0,0,0.05));
}

.unanswered-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #E6A23C;
  margin-right: 4px;
  flex-shrink: 0;
  position: relative;
  top: -1px;
}

.catalog-section .el-tree {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-bottom: 60px;
}

.catalog-section :deep(.el-tree-node) {
  white-space: normal;
}

/* Right Sidebar Styles */
.right-sidebar {
  width: 400px;
  height: 100%;
  background-color: var(--bg-sidebar);
  border-left: 1px solid var(--border-color);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 20px rgba(var(--primary-rgb), 0.05);
}

@supports not (height: 100dvh) {
  .right-sidebar {
    height: 100vh;
  }
}

.right-sidebar.collapsed {
  width: 0;
  border-left: none;
  box-shadow: none;
}

.right-sidebar .sidebar-content {
  flex: 1;
  height: 100%;
  overflow: hidden;
  padding: 0;
}

/* AI sidebar is now fully user-controlled, no auto-collapse based on screen width */

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  overflow-x: hidden;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

/* Allow main content to flex naturally to accommodate sidebars */

.top-bar {
  min-height: 60px;
  background-color: #fff;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 10px 15px;
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  box-sizing: border-box;
  gap: 10px;
}

.top-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  flex-wrap: wrap;
}

.top-bar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: 10px;
}

/* Reduce spacing on smaller screens */
@media (max-width: 900px) {
  .top-bar {
    padding: 8px 12px;
    gap: 6px;
  }

  .top-bar-left {
    gap: 4px;
  }

  .top-bar-right {
    gap: 4px;
  }

  .preview-mode-tag {
    font-size: 12px;
    padding: 4px 8px;
  }
}

@media (max-width: 768px) {
  .top-bar {
    padding: 8px 10px;
  }
}

.ai-toggle {
  background-color: white;
  color: var(--primary);
  border: 1px solid var(--primary);
  white-space: nowrap;
  flex-shrink: 0;
}

.ai-toggle:hover {
  background-color: var(--primary-light);
  border-color: var(--primary-hover);
  color: var(--primary-hover);
}

.content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overflow-x: hidden;
  height: 100vh;
  max-height: 100vh;
  width: 100%;
}

.post-card-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 10px 20px 0 20px;
  overflow: visible; /* Changed from hidden to visible so side buttons are not clipped */
  width: 100%;
  min-height: 0;
  margin-bottom: 0;
  position: relative; /* For side navigation buttons positioning */
}

.floating-controls {
  position: fixed;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  padding: 4px 8px;
  border-radius: 12px;
  border: 1px solid rgba(var(--primary-rgb), 0.1);
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255,255,255,0.6);
}

.floating-controls-left {
  top: 10px;
  transition: left 0.3s ease;
}

.floating-controls-right {
  bottom: 30px;
  right: 20px;
  flex-direction: column;
  gap: 6px;
  transition: right 0.3s ease;
}

.floating-controls-right .floating-btn {
  width: 90px;
  text-align: center;
  justify-content: center;
  padding: 5px 0;
  box-sizing: border-box;
  white-space: nowrap;
  display: block;
  margin: 0;
}

.floating-controls-bottom-left {
  bottom: 30px;
  transition: left 0.3s ease;
}

.back-btn {
  font-weight: 600;
  background: var(--primary) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 8px;
  padding: 5px 14px;
}

.back-btn:hover,
.back-btn:focus,
.back-btn:active {
  background: var(--primary-dark) !important;
  color: #fff !important;
  border: none !important;
  box-shadow: 0 3px 12px rgba(var(--primary-rgb), 0.4) !important;
}

.floating-btn {
  border: none;
  background: transparent;
  color: var(--text-secondary);
  padding: 5px 9px;
  font-size: 14px;
  white-space: nowrap;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.floating-btn:hover {
  color: var(--primary);
  background: var(--primary-light);
}

.floating-btn:focus {
  outline: none;
  background: transparent;
}

.ai-btn {
  font-weight: 600;
  background: var(--primary) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 8px;
}

.ai-btn:hover,
.ai-btn:focus,
.ai-btn:active {
  background: var(--primary-dark) !important;
  color: #fff !important;
  border: none !important;
  box-shadow: 0 3px 12px rgba(var(--primary-rgb), 0.4) !important;
}

.ai-btn-active {
  animation: none !important;
  background: #4a5568 !important;
  color: #fff !important;
  border: none !important;
}

.ai-btn-active:hover,
.ai-btn-active:focus,
.ai-btn-active:active {
  background: #374151 !important;
  color: #fff !important;
  border: none !important;
  box-shadow: none !important;
}

.note-sidebar-btn {
  font-weight: 600;
  background: var(--primary) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 8px;
}

.note-sidebar-btn:hover,
.note-sidebar-btn:focus,
.note-sidebar-btn:active {
  background: var(--primary-dark) !important;
  color: #fff !important;
  border: none !important;
  box-shadow: 0 3px 12px rgba(var(--primary-rgb), 0.4) !important;
}

.note-sidebar-btn-active {
  background: #4a5568 !important;
  color: #fff !important;
  border: none !important;
}

.note-sidebar-btn-active:hover,
.note-sidebar-btn-active:focus,
.note-sidebar-btn-active:active {
  background: #374151 !important;
  color: #fff !important;
  border: none !important;
  box-shadow: none !important;
}

.note-sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.note-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #eef2f8;
  flex-shrink: 0;
}

.note-sidebar-header h3 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.note-sidebar-hint {
  font-size: 12px;
  color: var(--text-muted, #909399);
  margin: 0;
}

.note-sidebar-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.note-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #909399;
}

.note-empty p {
  margin: 0;
  font-size: 13px;
}

.note-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.note-card {
  background: #f8faff;
  border: 1px solid #eef2f8;
  border-radius: 10px;
  padding: 14px 16px;
  transition: all 0.2s;
}

.note-card:hover {
  border-color: rgba(var(--primary-rgb), 0.2);
  box-shadow: 0 2px 8px rgba(var(--primary-rgb), 0.06);
}

.note-card-editing {
  border-color: var(--primary);
  background: #f0f7ff;
}

.note-card-content {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary, #303133);
  white-space: pre-wrap;
  word-break: break-word;
}

.note-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #eef2f8;
}

.note-card-time {
  font-size: 11px;
  color: #909399;
}

.note-card-actions {
  display: flex;
  gap: 4px;
}

.note-card-actions .el-button {
  padding: 4px;
  color: #909399;
}

.note-card-actions .el-button:hover {
  color: var(--primary);
}

.note-sidebar-input {
  flex-shrink: 0;
  padding: 16px 20px;
  border-top: 1px solid #eef2f8;
  background: #fafbfd;
}

.note-sidebar-input .el-textarea :deep(textarea) {
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  font-size: 13px;
  line-height: 1.5;
}

.note-sidebar-input .el-textarea :deep(textarea):focus {
  border-color: var(--primary);
}

.note-sidebar-input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

@keyframes aiPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--primary-rgb), 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(var(--primary-rgb), 0); }
}

.floating-tag {
  cursor: pointer;
  white-space: nowrap;
}

.floating-tag.clickable:hover {
  opacity: 0.8;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  width: 100%;
  height: 100%;
  max-width: 900px;
  margin: 0 auto;
  gap: 0;
  padding-bottom: 0;
  padding-top: 5px;
  overflow: hidden; /* Contain content overflow */
}

.post-card {
  flex: 1;
  width: auto;
  max-height: 100%;
  aspect-ratio: 0.618;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  border-radius: 18px;
  transition: box-shadow 0.4s ease;
}

/* Card size is now height-based with dynamic width from aspect ratio */

:deep(.post-card.el-card) {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 80%;
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-card);
  background: var(--bg-card);
}

:deep(.post-card .el-card__body) {
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

.post-header {
  margin: 10px 10px;
  min-height: 60px;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.post-header.placeholder {
  visibility: hidden; /* Hide the placeholder but maintain the space */
}

.group-info {
  color: #909399;
  font-size: 16px;
  margin-bottom: 8px;
}

.post-title {
  margin: 0;
  color: #303133;
  font-size: 20px;
}

.answer-display {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 0px;

}

.answer-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 2px 0 0 0;
  z-index: 10;
  flex-shrink: 0;
  flex-grow: 0;
  width: 100%;
  margin-bottom: 16px;
}

.answer-controls > .answer-number {
  order: -1;
  margin-top: 10px !important;
  margin-bottom: 0 !important;
}

.navigation-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px !important;
}

.answer-navigation {
  display: flex;
  flex-direction: row;
  gap: 6px !important;
}

.nav-button {
  position: relative;
  z-index: 2;
}

.nav-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #cbd5e0;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-dot.active {
  background: var(--primary);
  width: 18px;
  border-radius: 3px;
  box-shadow: 0 1px 6px rgba(var(--primary-rgb), 0.4);
}

.nav-dot:hover {
  background-color: var(--primary-hover);
  transform: scale(1.3);
}

/* Side navigation buttons */
.side-nav-button {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 9998;
  width: 40px;
  height: 40px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 12px rgba(var(--primary-rgb), 0.1);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  color: var(--text-secondary);
}

.side-nav-button:hover:not(:disabled) {
  background: var(--primary);
  border-color: transparent;
  color: white;
  transform: translateY(-50%) scale(1.06);
  box-shadow: 0 4px 16px rgba(var(--primary-rgb), 0.35);
}

.side-nav-button:active:not(:disabled) {
  transform: translateY(-50%) scale(0.95);
}

.side-nav-button:disabled {
  opacity: 0.2;
  cursor: not-allowed;
  background-color: rgba(255, 255, 255, 0.5);
}

.side-nav-button i {
  font-size: 18px;
  font-weight: bold;
}

.post-content {
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0px;
  cursor: pointer;
  animation: contentFadeIn 0.3s ease;
}

@keyframes contentFadeIn {
  0% { opacity: 0.6; }
  100% { opacity: 1; }
}

.answer-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 32px;
  overflow-y: auto;
  padding: 24px;
  scroll-behavior: smooth;
}

.answer-content::-webkit-scrollbar {
  width: 3px;
}

.answer-content::-webkit-scrollbar-thumb {
  background: rgba(var(--primary-rgb), 0.2);
  border-radius: 4px;
}

.answer-content::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--primary-rgb), 0.4);
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: flex-start;
  padding-right: 8px;
  white-space: normal;
  word-break: break-word;
}

.is-group {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 13px;
}

.is-post {
  color: var(--text-secondary);
  transition: color 0.2s ease;
  font-size: 13px;
}

.is-current {
  color: var(--primary) !important;
  font-weight: 600;
}

.is-locked {
  color: #b8c5d6 !important;
  opacity: 0.7;
}

.lock-icon {
  margin-right: 5px;
  color: #b8c5d6;
}

.is-paywalled {
  color: #e6a23c !important;
  opacity: 0.8;
}

/* Paywall overlay */
.paywall-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  border-radius: 8px;
}

.paywall-cta {
  text-align: center;
  padding: 40px;
}

.paywall-lock-icon {
  font-size: 48px;
  color: #e6a23c;
  margin-bottom: 16px;
}

.paywall-cta h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #303133;
}

.paywall-cta p {
  margin: 0 0 20px 0;
  color: #909399;
  font-size: 14px;
}

.content-blurred {
  filter: blur(8px);
  pointer-events: none;
  user-select: none;
}

:deep(.el-tree-node__content) {
  height: auto;
  min-height: 36px;
  padding: 6px 4px;
  border-radius: 8px;
  transition: background 0.15s;
}

:deep(.el-tree-node.is-current > .el-tree-node__content) {
  background: var(--primary-light);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
}

:deep(.el-tree-node__content:hover) {
  background: rgba(var(--primary-rgb), 0.04);
}

:deep(.el-tree-node__label) {
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

.answer-card {
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

:deep(.answer-card.el-card) {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: none;
  box-shadow: none;
  border-radius: 12px;
  background: transparent;
}

:deep(.answer-card .el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0;
  overflow: hidden;
}

.text-content, .image-content, .video-content, .youtube-content, .quiz-content {
  min-height: 100%;
}

.youtube-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.youtube-content iframe {
  width: 100%;
  max-width: 800px;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.quiz-content {
  pointer-events: auto;
  position: relative;
  z-index: 1;
  padding: 20px;
}

.card-footer {
  position: absolute;
  bottom: 0px;
  left: 0;
  right: 0;
  height: 20px;
  border-top: 1px solid var(--border-subtle);
  padding: 6px;
  display: flex;
  justify-content: flex-end;
  z-index: 1;
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(8px);
}

.answer-number {
  color: var(--text-muted);
  font-size: 11px;
  margin-left: 10px;
  font-weight: 500;
  letter-spacing: 0.03em;
}

.quiz-option {
  border: 1.5px solid #dde4ee;
  border-radius: 10px;
  padding: 13px 18px;
  margin-bottom: 8px;
  margin-top: 8px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: #f8fafc;
  pointer-events: auto;
}

.quiz-option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
}

.correct-marker {
  margin-left: 10px;
  color: #67c23a;
  padding: 10px;
  font-size: 16px;
}

.incorrect-marker {
  margin-left: 10px;
  color: #F56C6C;
  padding: 10px;
  font-size: 16px;
}

.quiz-option.selected {
  background-color: var(--primary-light);
  border-color: var(--primary);
  box-shadow: 0 2px 8px rgba(var(--primary-rgb), 0.15);
}

.quiz-option.correct {
  background-color: #f0faf0;
  border-color: #52c41a;
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.12);
}

.quiz-option.incorrect {
  background-color: #fff2f0;
  border-color: #ff4d4f;
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.12);
}

.quiz-option:hover:not(.not-clickable) {
  border-color: var(--primary);
  background-color: var(--primary-light);
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(var(--primary-rgb), 0.12);
}

.image-content img {
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.nav-button-placeholder {
  width: 40px;
  height: 40px;
  position: relative;
  z-index: 2;
}

@media screen and (min-width: 1440px) {
  .collection-title {
    font-size: 20px;
  }
  
  .group-info {
    font-size: 20px;
  }
  
  .post-title {
    font-size: 24px;
  }
  
  .text-content, .image-content, .video-content, .youtube-content, .quiz-content {
    font-size: 20px;
  }
}

:deep(.el-card) {
  border: none;
  border-radius: 12px;
}

.preview-mode-tag {
  margin-left: 8px;
  flex-shrink: 0;
  white-space: nowrap;
}

.preview-mode-tag.clickable {
  cursor: pointer;
  transition: all 0.3s ease;
}

.preview-mode-tag.clickable:hover {
  transform: scale(1.04);
  box-shadow: 0 2px 8px rgba(230, 162, 60, 0.3);
}

.preview-mode-tag i {
  margin-right: 5px;
}

.extra-info-note {
  color: #606266;
  font-size: 14px;
}

.answer-header {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.quiz-explanation {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #f4f8ff 0%, var(--primary-light) 100%);
  border-radius: 10px;
  border-left: 4px solid var(--primary);
}

.quote-content {
  margin-bottom: 15px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f0faf8 0%, #f5fffe 100%);
  border-radius: 10px;
  border-left: 4px solid #36c3a0;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.65;
}

.explanation-header {
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 8px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.explanation-content {
  font-size: 15px;
  line-height: 1.65;
  color: var(--text-secondary);
}

/* Apply Markdown styles to explanation content */
.explanation-content /deep/ h1,
.explanation-content /deep/ h2,
.explanation-content /deep/ h3,
.explanation-content /deep/ h4,
.explanation-content /deep/ h5,
.explanation-content /deep/ h6 {
  font-weight: 600;
  line-height: 1.4;
  margin-top: 16px;
  margin-bottom: 12px;
  color: #303133;
}

.explanation-content /deep/ h2 {
  font-size: 1.3em;
}

.explanation-content /deep/ h3 {
  font-size: 1.15em;
}

.explanation-content /deep/ pre {
  background-color: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px;
  overflow-x: auto;
  margin: 12px 0;
}

.explanation-content /deep/ code {
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9em;
  color: #e96900;
}

.explanation-content /deep/ pre code {
  background-color: transparent;
  padding: 0;
  color: #303133;
}

.explanation-content /deep/ ul,
.explanation-content /deep/ ol {
  padding-left: 1.5em;
  margin: 8px 0;
}

.explanation-content /deep/ li {
  margin: 6px 0;
}

.explanation-content /deep/ p {
  margin: 10px 0;
}

.explanation-content /deep/ strong {
  font-weight: 600;
}

.explanation-content /deep/ a {
  color: #409eff;
  text-decoration: none;
}

.explanation-content /deep/ a:hover {
  text-decoration: underline;
}

/* Apply Markdown styles to quiz question */
.quiz-question /deep/ h1,
.quiz-question /deep/ h2,
.quiz-question /deep/ h3,
.quiz-question /deep/ h4,
.quiz-question /deep/ h5,
.quiz-question /deep/ h6 {
  font-weight: 600;
  margin: 12px 0 8px 0;
}

.quiz-question /deep/ code {
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9em;
  color: #e96900;
}

.quiz-question /deep/ strong {
  font-weight: 600;
}

/* Apply Markdown styles to quiz options */
.quiz-option /deep/ code {
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9em;
  color: #e96900;
}

.quiz-option /deep/ strong {
  font-weight: 600;
}

.text-content {
  font-size: 18px;
  line-height: 1.75;
  color: var(--text-primary);
  letter-spacing: 0.01em;
}

.quiz-question {
  font-size: 18px;
  line-height: 1.65;
  color: var(--text-primary);
  font-weight: 500;
}

.placeholder-controls {
  opacity: 0;
  pointer-events: none;
}

.star-button {
  margin-top: 0px;
  height: 20px;
  padding: 0px;
  font-size: 22px;
  color: #b0b8c8;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1;
}

.star-button:hover {
  transform: scale(1.15);
  color: #f5a623;
}

.star-button.is-starred {
  color: #f5a623;
}

.star-button i {
  transition: all 0.3s ease;
}

.note-button {
  margin-top: 0px;
  height: 20px;
  padding: 0px;
  font-size: 22px;
  color: #b0b8c8;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1;
}

.note-button:hover {
  transform: scale(1.15);
  color: var(--primary);
}

.fullscreen-button {
  margin-top: 0px;
  height: 20px;
  padding: 0px;
  font-size: 22px;
  color: #b0b8c8;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1;
}

.fullscreen-button:hover {
  transform: scale(1.15);
  color: var(--primary);
}

.note-textarea {
  margin: 10px 0;
  width: 100%;
}

:deep(.note-textarea .el-textarea__inner) {
  resize: none;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
  padding: 12px;
  background-color: #f8f9fa;
  border: 1px solid #dcdfe6;
}

:deep(.note-textarea .el-textarea__inner:focus) {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
}

.note-label {
  font-size: 14px;
  color: #67c23a;  /* Element UI's success color */
  font-weight: bold;
  margin-bottom: 8px;
  padding-left: 4px;
}

.foot-note-label {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  font-style: italic;
}

.quiz-count {
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 500;
}

@media screen and (min-width: 1440px) {
  .quiz-count {
    font-size: 14px;
  }
}

.quiz-progress-bar {
  display: flex;
  gap: 4px;
  margin-top: 8px;
  margin-bottom: 8px;
}

.progress-segment {
  flex: 1;
  height: 3px;
  background-color: #dde4ee;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.progress-segment:hover {
  transform: scaleY(1.6);
}

.progress-segment.completed {
  background: var(--primary);
}

.answer-actions {
  display: flex;
  gap: 10px;
  margin-right: 10px;
  align-items: center;
}

.word-count-chip {
  font-size: 11px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 10px;
  color: #67c23a;
  background-color: #f0f9eb;
  border: 1px solid #d6ebc3;
  white-space: nowrap;
  user-select: none;
}

.full-screen-toggle {
  margin-left: 10px;
}

.post-card-container.full-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  background: var(--bg-page);
  padding: 20px;
  margin: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.post-card-container.full-screen .content-wrapper {
  max-width: 1200px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
}

.post-card-container.full-screen .post-card {
  width: 100%;
  height: 100%;
  max-width: none;
  aspect-ratio: auto;
}

/* Ensure the answer content is centered and properly sized in fullscreen */
.post-card-container.full-screen .answer-content {
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px;
}

/* Adjust navigation controls position in fullscreen */
.post-card-container.full-screen .answer-controls {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
}

/* Adjust font sizes in full-screen mode */
.post-card-container.full-screen .text-content {
  font-size: 24px;
  line-height: 1.6;
}

.post-card-container.full-screen .quiz-question {
  font-size: 24px;
  line-height: 1.6;
}

.post-card-container.full-screen .quiz-option-content {
  font-size: 20px;
}

.post-card-container.full-screen .quiz-explanation {
  margin-top: 30px;
  padding: 20px;
}

.post-card-container.full-screen .explanation-content {
  font-size: 20px;
  line-height: 1.6;
}

.post-card-container.full-screen .quote-content {
  font-size: 20px;
  line-height: 1.6;
  padding: 20px;
  margin-bottom: 20px;
}

.post-card-container.full-screen .note-content-text {
  font-size: 20px;
  line-height: 1.6;
}

.post-card-container.full-screen .note-label {
  font-size: 18px;
}

.post-card-container.full-screen .foot-note-label {
  font-size: 14px;
}

/* Adjust quiz-related elements in full-screen */
.post-card-container.full-screen .quiz-option {
  padding: 20px 30px;
  margin-bottom: 20px;
  margin-top: 20px;
}

.post-card-container.full-screen .correct-marker,
.post-card-container.full-screen .incorrect-marker {
  font-size: 24px;
  padding: 15px;
}

/* Settings Dialog Styles */
.settings-container {
  padding: 20px 0;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #eef2f8;
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-label {
  flex: 1;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.no-settings {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.no-settings p {
  margin: 0;
  font-size: 14px;
}

.settings-button {
  margin-left: 10px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  color: var(--primary);
}

.loading-container p {
  margin-top: 20px;
  font-size: 16px;
  color: var(--text-secondary);
}

/* Custom Modal Styles */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 30, 60, 0.35);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.custom-modal {
  background: white;
  border-radius: 20px;
  width: 480px;
  max-width: 90vw;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(var(--primary-rgb), 0.15), 0 4px 20px rgba(0,0,0,0.1);
  border: 1px solid var(--border-color);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eef2f8;
  background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
}

.modal-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: var(--text-muted);
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  color: var(--primary);
  background: var(--primary-light);
}

.modal-body {
  padding: 20px 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #eef2f8;
  text-align: right;
  background: #fafbfd;
}

/* Markdown Editor Sidebar */
.markdown-editor-sidebar {
  width: 500px;
}

.markdown-editor-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.markdown-editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.markdown-editor-header h3 {
  margin: 0 0 2px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.markdown-editor-hint {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
}

.markdown-editor-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.markdown-editor-header-actions .close-btn {
  background: none;
  border: none;
  font-size: 22px;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0 0 0 4px;
  line-height: 1;
}

.markdown-editor-header-actions .close-btn:hover {
  color: var(--text-primary);
}

.markdown-editor-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.markdown-readonly {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.markdown-source {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-secondary);
  background: transparent;
}

.markdown-textarea {
  flex: 1;
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  padding: 16px 20px;
  font-family: 'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-primary);
  background: #fcfcfe;
  box-sizing: border-box;
}

.markdown-textarea:focus {
  background: #fff;
}

.markdown-textarea::placeholder {
  color: var(--text-muted);
}

</style> 